import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import {
  araEditAccept,
  araEditLog,
  renderAra,
  streamAra,
  streamAraEdit,
  type AraAttachment,
  type AraEditChatMessage,
  type AraEditChatRequest,
  type AraGenerateRequest
} from '@/features/ara-generation/api/ara-api'
import type { AraConfigurePayload } from '@/features/ara-generation/types/ara-configure'
import type {
  AraEditFrame,
  AraEditMetaFrame,
  AraEditMetaPatch,
  AraEditSectionFrame,
  AraEditTarget,
  AraRemoveIntent
} from '@/features/ara-generation/types/ara-events'
import {
  ARA_CHAT_EDIT_KEY_PREFIX,
  ARA_META_SECTION_KEY
} from '@/features/ara-generation/types/ara-events'
import {
  hasAssistantMessageContent,
  type ChatAssistantMessage,
  type ChatChartProposal,
  type ChatClarifyOption,
  type ChatProposalStatus,
  type ChatRemoveProposal,
  type ChatSectionProposal,
  type ChatSectorRebuild,
  type ChatSectorRebuildStatus,
  type ChatSpanProposal,
  type ChatTargetProposal,
  type ChatUserMessage
} from '@/features/ara-generation/types/ara-chat'
import { araPlantName } from '@/features/ara-generation/utils/ara-dirty'
import { removeIntentFromFrame } from '@/features/ara-generation/utils/remove-proposal'
import type { PartialGenerationBrief } from '@/features/ara-generation/types/generation-brief'
import {
  activeVariant as selectActiveVariant,
  emptyVariant,
  emptyWorkspace,
  type AraDraftIntake,
  type AraDraftPayload,
  type AraGenerationPhase,
  type AraGenerationStage,
  type AraRequestedSection,
  type AraSectionProgress,
  type AraVariant,
  type AraVariantEditState,
  type AraWorkspace
} from '@/features/ara-generation/types/ara-workspace'
import { conversationalAraResponse } from '@/features/ara-generation/utils/ara-chat-response'
import {
  applyTextOverridesToSections,
  assembleBodyHtml,
  extractTargetText,
  replaceSectionBody,
  spliceTargetAcrossSections,
  splitRoleHtmlIntoSections,
  type SpliceTargetAnchor
} from '@/features/ara-generation/utils/ara-section-html'
import { normalizeTargetDraftHtml } from '@/features/ara-generation/utils/ara-target-normalize'
import { stripAraEditArtifacts } from '@/features/ara-generation/utils/ara-edit-artifacts'
import { araCurrencyFor, araLanguageFor } from '@/features/ara-generation/utils/ara-locale'
import { spliceSpanAcrossSections } from '@/features/ara-generation/utils/ara-span-edit'
import { removeColumn } from '@/features/ara-generation/utils/ara-remove-column'
import { removeElement } from '@/features/ara-generation/utils/ara-remove-element'
import {
  captureSectionState,
  collectSubtreeEditKeys,
  dedupeUndoSnapshots,
  dropOrphanedScope,
  pruneTextOverrides,
  restoreBatchSnapshots,
  type SectionState
} from '@/features/ara-generation/utils/ara-removal-state'
import {
  applyChartProposalToState,
  collectChartProposal
} from '@/features/ara-generation/utils/ara-chart-proposal'
import {
  ARA_BUILTIN_KEYS,
  ARA_SECTION_CATALOG,
  canonicalizeAraSectionKey,
  canonicalizeAraSectionKeys,
  canonicalizeAraSectionOrder,
  migrateAraCustomSections
} from '@/features/ara-generation/utils/ara-sections'
import { toMasterAraStreamRequest } from '@/features/ara-generation/utils/strip-ara-stream-additive-fields'
import {
  createIndexedDbWorkspaceDb,
  sanitizeWorkspaceForPersistence,
  type AraWorkspaceDb
} from '@/features/ara-generation/utils/ara-workspace-db'
import {
  buildAraSourcePayload,
  decodeAraSource,
  encodeAraSource,
  type AraSourcePayload
} from '@/features/ara-generation/utils/ara-source'
import { createAraDraftDocument } from '@/features/plant-assessment/api/documents-draft-create'
import {
  getDocumentSource,
  putDocumentSource
} from '@/features/plant-assessment/api/documents-source'
import { documentsKeys } from '@/features/plant-assessment/api/query-keys'
import { paths } from '@/routes/routes'

export type {
  AraDraftIntake,
  AraDraftPayload,
  AraGenerationPhase,
  AraGenerationStage,
  AraRequestedSection,
  AraSectionProgress,
  AraVariant,
  AraVariantEditState,
  AraWorkspace
}

export type AraSessionsProviderProps = {
  children: ReactNode
  db?: AraWorkspaceDb
  /** Test seam: override the trailing persistence debounce (defaults to ARA_PERSIST_DEBOUNCE_MS). */
  persistDebounceMs?: number
  /** Test seam: override the network leg bound (defaults to ARA_SYNC_TIMEOUT_MS). */
  syncTimeoutMs?: number
}

/**
 * Sync state for one assessment workspace, exposed for the panel header
 * Saved indicator. `pending` means content is ahead of the server; local
 * IndexedDB persistence does not change this state.
 */
export type AraSaveState = {
  status: 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  lastSavedAt: number | null
}

const IDLE_SAVE_STATE: AraSaveState = { status: 'idle', lastSavedAt: null }

/** The document-list shape the sidebar passes to openServerDocument. */
export type AraServerDocumentRef = {
  id: string
  title: string
  status: 'draft' | 'published'
}

/**
 * Trailing debounce for the persistence batch (2–5s per ADR 0001): a typing
 * burst re-arms the timer, so the flush lands once the user pauses instead of
 * churning per keystroke.
 */
export const ARA_PERSIST_DEBOUNCE_MS = 3000

/** Upper bound for the idle wait once the debounce fires (requestIdleCallback timeout). */
const ARA_PERSIST_IDLE_TIMEOUT_MS = 2000

/**
 * Upper bound for any server leg of the autosave/open pipeline. The axios
 * layer has no timeout either (apiScoping), and its request interceptor
 * awaits the auth token BEFORE the axios timeout clock even starts — so
 * without this bound a hung request (auth stall, blackholed endpoint) pins
 * the save indicator in 'saving' forever and wedges the document open. On
 * timeout the failure surfaces as saveState 'error' and the sync marker is
 * retained, so the next batch retries.
 */
export const ARA_SYNC_TIMEOUT_MS = 20_000

/**
 * Race a network leg against a hard bound so a hung promise can never pin
 * the pipeline. The losing promise is left to settle on its own in the
 * background — a late success is still correct (the sync idempotently
 * re-PUTs the same content). Callers treat a timeout exactly like a failed
 * request, so the rejection needs no distinguishing marker.
 */
function withSyncTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let clear = () => {}
  const bound = new Promise<never>((_resolve, reject) => {
    const handle = setTimeout(() => reject(new Error('ARA sync timed out')), timeoutMs)
    clear = () => clearTimeout(handle)
  })
  const raced = Promise.race([promise, bound])
  void raced.then(clear, clear)
  return raced
}

/**
 * Per-variant buffer holding edited content OUTSIDE React render state until
 * the idle batch applies it: template text overrides (per-keystroke) and
 * inline section bodies. This is what keeps typing off the re-render hot path.
 */
type AraEditBuffer = {
  textOverrides: Record<string, string>
  sectionBodies: Record<string, string>
}

export type StartGenerationOptions = {
  brief?: PartialGenerationBrief | null
}

export type AraSessionsContextValue = {
  workspaces: Record<string, AraWorkspace>
  hydrated: boolean
  saveStates: Record<string, AraSaveState>
  isWorkspaceReady: (assessmentId: string) => boolean
  getWorkspace: (assessmentId: string) => AraWorkspace
  /**
   * Synchronous, ref-fresh read of a variant's edit state — for event
   * handlers that flushed buffered edits in the same tick (publish render,
   * edit-chat send) and must NOT read the pre-flush React snapshot.
   */
  readVariantEdit: (assessmentId: string, variantId: string) => AraVariantEditState | null
  startGeneration: (
    assessmentId: string,
    configure: AraConfigurePayload,
    options?: StartGenerationOptions
  ) => Promise<string>
  stopGeneration: (assessmentId: string, variantId: string) => void
  retryGeneration: (assessmentId: string, variantId: string) => Promise<void>
  /**
   * Full re-run of a finished variant with a NEW configure payload (SCP-129
   * regenerate): the run resets the variant's edit state, so manual edits are
   * discarded — the caller must have confirmed that loss first.
   */
  regenerateVariant: (
    assessmentId: string,
    variantId: string,
    configure: AraConfigurePayload
  ) => Promise<void>
  removeVariant: (assessmentId: string, variantId: string) => void
  clearWorkspace: (assessmentId: string) => void
  setActiveVariant: (assessmentId: string, variantId: string | null) => void
  openConfigure: (assessmentId: string) => void
  closeConfigure: (assessmentId: string) => void
  saveDraftIntake: (assessmentId: string, draft: AraDraftIntake) => void
  clearDraftIntake: (assessmentId: string) => void
  sendEditMessage: (
    assessmentId: string,
    variantId: string,
    content: string,
    attachments?: AraAttachment[],
    choices?: { resolvedTarget?: ChatClarifyOption }
  ) => Promise<void>
  resolveClarifyOption: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    option: ChatClarifyOption
  ) => Promise<void>
  stopEditStreaming: (assessmentId: string, variantId: string) => void
  previewProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: AraEditFrame
  ) => Promise<void>
  applyProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: AraEditFrame
  ) => Promise<void>
  rejectProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ) => void
  previewTargetProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatTargetProposal
  ) => Promise<void>
  applyTargetProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatTargetProposal
  ) => Promise<void>
  rejectTargetProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ) => void
  previewSpanProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatSpanProposal
  ) => Promise<void>
  applySpanProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatSpanProposal
  ) => Promise<void>
  rejectSpanProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ) => void
  previewChartProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatChartProposal
  ) => Promise<void>
  applyChartProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatChartProposal
  ) => Promise<void>
  rejectChartProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ) => void
  previewRemoveProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ) => Promise<void>
  acceptRemoveProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ) => Promise<boolean>
  rejectRemoveProposal: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ) => void
  /**
   * Rebuild the report against a cohort the SE picked from the chat's options.
   * Not an edit: the sector drives every benchmark, so the figures only exist
   * after the pipeline re-runs. Pinned onto `configure` so a retry keeps it.
   *
   * The click is itself the SE's choice, so a clean draft rebuilds straight
   * away. `dirty` routes the choice through the confirm card instead, because a
   * rebuild discards unsaved work and one click should not be able to do that
   * silently. `messageId` is the message whose options were clicked — the card
   * renders on it.
   */
  chooseSector: (
    assessmentId: string,
    variantId: string,
    messageId: string,
    sector: string,
    dirty: boolean
  ) => void
  /**
   * Start the rebuild a `regenerate` frame proposed. No-ops unless the message
   * still holds a `pending` offer, so a double-click or a click on an already
   * decided card cannot launch a second run.
   */
  confirmSectorRebuild: (assessmentId: string, variantId: string, messageId: string) => void
  /** Decline the offered rebuild. The draft is left exactly as it is. */
  cancelSectorRebuild: (assessmentId: string, variantId: string, messageId: string) => void
  /**
   * Deterministic removal (FE-6175): resolves the intent against the stored
   * section HTML, prunes overrides of removed edit keys in the same update and
   * re-renders the section. Returns false (and changes nothing) when the
   * target is unresolvable, the variant is read-only or an edit is streaming.
   */
  applyRemoveIntent: (
    assessmentId: string,
    variantId: string,
    intent: AraRemoveIntent,
    options?: { fromProposal?: boolean }
  ) => Promise<boolean>
  /**
   * Batch removal (FE-6248): applies every target with the single-removal
   * reducer, one re-render and one undo entry. Skipped (protected) targets
   * are counted, never removed. Returns null when nothing applied.
   */
  applyBatchRemoveIntent: (
    assessmentId: string,
    variantId: string,
    targets: AraRemoveIntent[]
  ) => Promise<{ removed: number; skipped: number } | null>
  undoRemoveIntent: (assessmentId: string, variantId: string) => Promise<boolean>
  updateInlineSection: (assessmentId: string, variantId: string, key: string, html: string) => void
  updateFocusedSection: (assessmentId: string, variantId: string, key: string | null) => void
  setEditScope: (assessmentId: string, variantId: string, target: AraEditTarget) => void
  clearEditScope: (assessmentId: string, variantId: string) => void
  onTextEdit: (assessmentId: string, variantId: string, key: string, value: string) => void
  flushEdits: (assessmentId: string) => void
  resetVariantEdit: (assessmentId: string, variantId: string, baseline: AraVariantEditState) => void
  saveNow: (assessmentId: string) => Promise<void>
  openServerDocument: (assessmentId: string, document: AraServerDocumentRef) => Promise<void>
  setVariantReadOnly: (assessmentId: string, variantId: string, readOnly: boolean) => void
}

const AraSessionsContext = createContext<AraSessionsContextValue | null>(null)

function controllerKey(assessmentId: string, variantId: string) {
  return `${assessmentId}::${variantId}`
}

function highestNameNumber(existing: AraVariant[], pattern: RegExp): number {
  return existing.reduce((max, variant) => {
    const parsed = Number(pattern.exec(variant.name)?.[1])
    return Number.isFinite(parsed) ? Math.max(max, parsed) : max
  }, 0)
}

function nextVariantName(existing: AraVariant[]) {
  // Derive from the highest existing number, not the count: removing a middle
  // variant would otherwise reuse a number still on screen (e.g. [1, 3] → 3).
  return `Variant ${highestNameNumber(existing, /^Variant (\d+)$/) + 1}`
}

function createVariantId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `variant-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

type LegacyAraConfigurePayload = Omit<
  AraConfigurePayload,
  'language' | 'currency' | 'attachments'
> & {
  language?: string | null
  currency?: string | null
  attachments?: AraConfigurePayload['attachments'] | null
}

function normalizeConfigureOnLoad(
  configure: LegacyAraConfigurePayload | null | undefined,
  appLanguage: string | undefined | null
): AraConfigurePayload | null {
  if (!configure) return null
  const { customSections, keyReplacements } = migrateAraCustomSections(configure.customSections)
  return {
    ...configure,
    sections: Array.isArray(configure.sections)
      ? canonicalizeAraSectionKeys(configure.sections, keyReplacements)
      : null,
    sectionOrder: canonicalizeAraSectionOrder(
      configure.sectionOrder ?? ARA_BUILTIN_KEYS,
      keyReplacements
    ),
    customSections,
    sectionGuidanceExtra: configure.sectionGuidanceExtra
      ? Object.fromEntries(
          Object.entries(configure.sectionGuidanceExtra).map(([key, guidance]) => [
            canonicalizeAraSectionKey(key, keyReplacements),
            guidance
          ])
        )
      : null,
    language: configure.language ?? araLanguageFor(appLanguage),
    currency: configure.currency ?? araCurrencyFor(appLanguage),
    // Runs on every load of a persisted workspace. Dropping it here would mean a corrected
    // sector survived the session and then quietly reverted the next time the SE opened the
    // report — the kind of regression nobody reports because the page looks fine.
    sector: configure.sector ?? null,
    attachments: configure.attachments ?? []
  }
}

function cloneConfigure(configure: AraConfigurePayload): AraConfigurePayload {
  return {
    sections: configure.sections ? [...configure.sections] : null,
    sectionOrder: [...configure.sectionOrder],
    customSections: configure.customSections.map(section => ({ ...section })),
    styleGuideExtra: configure.styleGuideExtra ?? null,
    sectionGuidanceExtra: configure.sectionGuidanceExtra
      ? { ...configure.sectionGuidanceExtra }
      : null,
    language: configure.language,
    currency: configure.currency,
    // Explicit: this clone is what `retryGeneration` passes to the next run, so omitting
    // the field would silently revert an SE's sector correction to the wrong CRM value.
    sector: configure.sector ?? null,
    // Same retry argument: a scoped run retried must stay scoped. `null` = every asset.
    includedAssetIds: configure.includedAssetIds ? [...configure.includedAssetIds] : null,
    attachments: configure.attachments.map(attachment => ({ ...attachment }))
  }
}

function cloneBrief(
  brief: PartialGenerationBrief | null | undefined
): PartialGenerationBrief | null {
  if (!brief) return null
  return structuredClone(brief)
}

function cloneDraftIntake(draft: AraDraftIntake): AraDraftIntake {
  return {
    messages: draft.messages.map(message => ({ ...message })),
    brief: structuredClone(draft.brief),
    updatedAt: draft.updatedAt
  }
}

function normalizeWorkspace(
  workspace: AraWorkspace,
  appLanguage: string | undefined | null
): AraWorkspace {
  const resetPreviewingStatus = <T extends { status: ChatProposalStatus }>(proposals: T[]): T[] =>
    proposals.map(proposal =>
      proposal.status === 'previewing' ? { ...proposal, status: 'idle' as const } : proposal
    )
  return {
    ...emptyWorkspace(workspace.assessmentId),
    ...workspace,
    draftIntake: workspace.draftIntake ?? null,
    lastConfigure: normalizeConfigureOnLoad(workspace.lastConfigure, appLanguage),
    variants: workspace.variants.map(variant => ({
      ...emptyVariant({ id: variant.id, name: variant.name }),
      ...variant,
      brief: variant.brief ?? null,
      configure: normalizeConfigureOnLoad(variant.configure, appLanguage),
      // Workspaces written before the stamp existed carry a saved scope without a
      // timestamp; the variant's creation doubles as the save reference, so assets
      // added to the assessment afterwards still flag as "new, not included".
      scopeSavedAt:
        variant.scopeSavedAt ??
        (variant.configure?.includedAssetIds && variant.configure.includedAssetIds.length > 0
          ? variant.createdAt
          : null),
      edit: variant.edit
        ? {
            ...variant.edit,
            messages: (variant.edit.messages ?? []).map(message => {
              if (message.role !== 'assistant') return message
              return {
                ...message,
                proposals: resetPreviewingStatus(message.proposals ?? []),
                targetedProposals: resetPreviewingStatus(message.targetedProposals ?? []),
                spanProposals: resetPreviewingStatus(message.spanProposals ?? []),
                chartProposals: resetPreviewingStatus(message.chartProposals ?? []),
                removeProposals: resetPreviewingStatus(message.removeProposals ?? [])
              }
            })
          }
        : null
    }))
  }
}

function requestedSections(configure: AraConfigurePayload): AraRequestedSection[] {
  const selected = configure.sections ? new Set(configure.sections) : null
  const customLabels = new Map(
    configure.customSections.map(section => [section.key, section.label])
  )
  const builtinLabels = new Map(ARA_SECTION_CATALOG.map(section => [section.key, section.labelKey]))
  const seen = new Set<string>()
  const ordered: AraRequestedSection[] = []

  for (const key of configure.sectionOrder) {
    if (seen.has(key)) continue
    if (!(customLabels.has(key) || selected === null || selected.has(key))) continue
    seen.add(key)
    ordered.push({ key, label: customLabels.get(key) ?? builtinLabels.get(key) ?? key })
  }

  if (selected) {
    for (const key of selected) {
      if (seen.has(key)) continue
      seen.add(key)
      ordered.push({ key, label: customLabels.get(key) ?? builtinLabels.get(key) ?? key })
    }
  }

  for (const [key, label] of customLabels) {
    if (seen.has(key)) continue
    seen.add(key)
    ordered.push({ key, label })
  }

  return ordered
}

function requestedSectionsFromInit(
  frameSections: Array<{ id: string; label: string }>,
  preferredOrder: AraRequestedSection[]
): AraRequestedSection[] {
  const labels = new Map(frameSections.map(section => [section.id, section.label]))
  const builtinLabelKeys = new Map(
    ARA_SECTION_CATALOG.map(section => [section.key, section.labelKey])
  )
  const seen = new Set<string>()
  const ordered: AraRequestedSection[] = []

  for (const section of preferredOrder) {
    if (!labels.has(section.key) || seen.has(section.key)) continue
    seen.add(section.key)
    ordered.push({
      key: section.key,
      label: builtinLabelKeys.get(section.key) ?? labels.get(section.key) ?? section.label
    })
  }

  for (const section of frameSections) {
    if (seen.has(section.id)) continue
    seen.add(section.id)
    ordered.push({ key: section.id, label: section.label })
  }

  return ordered
}

function sanitizeSectionGuidanceExtra(
  extras: Record<string, string> | null | undefined
): Record<string, string> | null {
  if (!extras) return null
  const cleaned: Record<string, string> = {}
  for (const [key, value] of Object.entries(extras)) {
    const trimmed = value.trim()
    if (trimmed) cleaned[key] = trimmed
  }
  return Object.keys(cleaned).length > 0 ? cleaned : null
}

function isMetaFrame(frame: AraEditFrame): frame is AraEditMetaFrame {
  return frame.section_key === ARA_META_SECTION_KEY
}

function applyMetaPatch(
  current: Record<string, unknown>,
  patch: AraEditMetaPatch
): Record<string, unknown> {
  return {
    ...current,
    ...Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined))
  }
}

function applyTargetProposalToState(
  edit: AraVariantEditState,
  sectionOrder: string[],
  proposal: ChatTargetProposal
): { sections: Record<string, string>; structuredAra: Record<string, unknown> } {
  const { target } = proposal
  const isGroupTarget = Boolean(target.group || target.type === 'group' || target.column)
  const key = isGroupTarget ? target.key || '' : target.key || proposal.targetKey
  const normalized = normalizeTargetDraftHtml(target, proposal.html)
  const anchor: SpliceTargetAnchor = {
    key: key || undefined,
    selector: target.selector || undefined,
    sectionKey: target.sectionKey || undefined,
    id: target.id || undefined,
    type: target.type,
    group: target.group || undefined,
    column: target.column || undefined
  }
  const { sections, matched } = spliceTargetAcrossSections(
    edit.sections,
    sectionOrder,
    anchor,
    normalized
  )
  if (matched) return { sections, structuredAra: edit.structuredAra }

  if (!key || isGroupTarget) {
    return { sections: edit.sections, structuredAra: edit.structuredAra }
  }

  const prevOverrides =
    (edit.structuredAra.text_overrides as Record<string, string> | undefined) ?? {}
  return {
    sections: edit.sections,
    structuredAra: {
      ...edit.structuredAra,
      text_overrides: { ...prevOverrides, [key]: extractTargetText(proposal.html) }
    }
  }
}

function applySpanProposalToState(
  edit: AraVariantEditState,
  sectionOrder: string[],
  proposal: ChatSpanProposal
): { sections: Record<string, string> } {
  const { target } = proposal
  const key = proposal.targetKey || target.key
  const start = target.span_start ?? 0
  const end = target.span_end ?? 0
  const { sections } = spliceSpanAcrossSections(
    edit.sections,
    sectionOrder,
    { key, start, end },
    proposal.text
  )
  return { sections }
}

export type AraRemoveIntentOutcome =
  | {
      ok: true
      sections: Record<string, string>
      structuredAra: Record<string, unknown>
      /** Pre-op state of the affected section, for the in-session undo stack. */
      undo: SectionState
      /** Present only when the removal changed the section order (whole section). */
      sectionOrder?: string[]
    }
  | { ok: false; reason: string }

function applyWholeSectionRemoval(
  edit: AraVariantEditState,
  sectionKey: string,
  sectionOrder: string[]
): AraRemoveIntentOutcome {
  const before = edit.sections[sectionKey]
  if (before === undefined || !sectionOrder.includes(sectionKey)) {
    return { ok: false, reason: 'not-found' }
  }
  const overrides = (edit.structuredAra.text_overrides as Record<string, string> | undefined) ?? {}
  const removedKeys = collectSubtreeEditKeys(before)
  const sections = { ...edit.sections }
  delete sections[sectionKey]
  return {
    ok: true,
    sections,
    structuredAra:
      removedKeys.length > 0
        ? { ...edit.structuredAra, text_overrides: pruneTextOverrides(overrides, removedKeys) }
        : edit.structuredAra,
    undo: captureSectionState({ sectionKey, html: before, overrides, order: sectionOrder }),
    sectionOrder: sectionOrder.filter(key => key !== sectionKey)
  }
}

function applyRemoveIntentToState(
  edit: AraVariantEditState,
  intent: AraRemoveIntent,
  sectionOrder: string[]
): AraRemoveIntentOutcome {
  if (intent.wholeSection) {
    return applyWholeSectionRemoval(edit, intent.sectionKey ?? '', sectionOrder)
  }
  const isColumnIntent = Boolean(intent.group && intent.column)
  const candidateKeys = intent.sectionKey
    ? intent.sectionKey in edit.sections
      ? [intent.sectionKey]
      : []
    : Object.keys(edit.sections)

  const overrides = (edit.structuredAra.text_overrides as Record<string, string> | undefined) ?? {}

  for (const sectionKey of candidateKeys) {
    const before = edit.sections[sectionKey] ?? ''
    if (!before) continue
    const result = isColumnIntent
      ? removeColumn(
          before,
          {
            group: intent.group ?? '',
            column: intent.column ?? '',
            sectionKey: intent.sectionKey
          },
          { stylesHtml: edit.lastRenderedHtml }
        )
      : removeElement(before, {
          key: intent.key,
          selector: intent.selector,
          id: intent.id,
          sectionKey
        })
    if (!result.ok) continue

    const keysAfter = new Set(collectSubtreeEditKeys(result.html))
    const removedKeys = collectSubtreeEditKeys(before).filter(key => !keysAfter.has(key))
    return {
      ok: true,
      sections: { ...edit.sections, [sectionKey]: result.html },
      structuredAra:
        removedKeys.length > 0
          ? { ...edit.structuredAra, text_overrides: pruneTextOverrides(overrides, removedKeys) }
          : edit.structuredAra,
      undo: captureSectionState({ sectionKey, html: before, overrides })
    }
  }

  if (!isColumnIntent && intent.key && !intent.sectionKey) {
    return removeChromeText(edit, intent.key, overrides)
  }

  return { ok: false, reason: isColumnIntent ? 'group-not-found' : 'not-found' }
}

function removeChromeText(
  edit: AraVariantEditState,
  key: string,
  overrides: Record<string, string>
): AraRemoveIntentOutcome {
  const inSection = Object.values(edit.sections).some(html =>
    collectSubtreeEditKeys(html).includes(key)
  )
  if (inSection || overrides[key] === '') return { ok: false, reason: 'not-found' }
  return {
    ok: true,
    sections: edit.sections,
    structuredAra: { ...edit.structuredAra, text_overrides: { ...overrides, [key]: '' } },
    undo: captureSectionState({ sectionKey: '', html: '', overrides })
  }
}

export type AraBatchRemoveOutcome =
  | {
      ok: true
      sections: Record<string, string>
      structuredAra: Record<string, unknown>
      /** Pre-batch state of every affected section, for the in-session undo stack. */
      undo: SectionState[]
      removed: number
      /** Targets the reducer refused (guarded/not-found): kept, reported as skipped. */
      skipped: number
    }
  | { ok: false; reason: string }

function applyBatchRemoveToState(
  edit: AraVariantEditState,
  targets: AraRemoveIntent[],
  sectionOrder: string[]
): AraBatchRemoveOutcome {
  let sections = edit.sections
  let structuredAra = edit.structuredAra
  const undo: SectionState[] = []
  let removed = 0
  let skipped = 0

  // Targets arrive in document order; removing from the end keeps the
  // positional selectors of earlier keyless siblings valid.
  for (const intent of [...targets].reverse()) {
    const outcome = applyRemoveIntentToState({ ...edit, sections, structuredAra }, intent, sectionOrder)
    if (!outcome.ok) {
      skipped += 1
      continue
    }
    // Keep-first per section: the first snapshot is the pre-batch state, so
    // restoring it alone rewinds every removal chained in that section. The
    // push site dedupes (dedupeUndoSnapshots) before the entry hits the stack.
    undo.push(outcome.undo)
    sections = outcome.sections
    structuredAra = outcome.structuredAra
    removed += 1
  }

  if (removed === 0) return { ok: false, reason: 'not-found' }
  return { ok: true, sections, structuredAra, undo, removed, skipped }
}

function mapVariant(
  workspace: AraWorkspace,
  variantId: string,
  update: (variant: AraVariant) => AraVariant
): AraWorkspace {
  return {
    ...workspace,
    variants: workspace.variants.map(variant =>
      variant.id === variantId ? update(variant) : variant
    )
  }
}

function findVariant(
  workspaces: Record<string, AraWorkspace>,
  assessmentId: string,
  variantId: string
): AraVariant | null {
  return workspaces[assessmentId]?.variants.find(variant => variant.id === variantId) ?? null
}

function findAssistantMessage(
  variant: AraVariant | null | undefined,
  messageId: string
): ChatAssistantMessage | undefined {
  return variant?.edit?.messages.find(
    candidate => candidate.id === messageId && candidate.role === 'assistant'
  ) as ChatAssistantMessage | undefined
}

function currentSectionProposal(
  variant: AraVariant | null | undefined,
  messageId: string,
  index: number
): ChatSectionProposal | undefined {
  return findAssistantMessage(variant, messageId)?.proposals[index]
}

function currentTargetProposal(
  variant: AraVariant | null | undefined,
  messageId: string,
  index: number
): ChatTargetProposal | undefined {
  return findAssistantMessage(variant, messageId)?.targetedProposals[index]
}

function currentSpanProposal(
  variant: AraVariant | null | undefined,
  messageId: string,
  index: number
): ChatSpanProposal | undefined {
  return findAssistantMessage(variant, messageId)?.spanProposals[index]
}

function currentChartProposal(
  variant: AraVariant | null | undefined,
  messageId: string,
  index: number
): ChatChartProposal | undefined {
  return findAssistantMessage(variant, messageId)?.chartProposals[index]
}

type ProposalAction =
  | { kind: 'section'; messageId: string; index: number; proposal: AraEditFrame }
  | { kind: 'target'; messageId: string; index: number; proposal: ChatTargetProposal }
  | { kind: 'span'; messageId: string; index: number; proposal: ChatSpanProposal }
  | { kind: 'chart'; messageId: string; index: number; proposal: ChatChartProposal }
  | { kind: 'remove'; messageId: string; index: number; proposal: ChatRemoveProposal }

function sectionProposalNextState(
  edit: AraVariantEditState,
  proposal: AraEditFrame,
  sectionOrder: string[]
) {
  const structuredAra = isMetaFrame(proposal)
    ? applyMetaPatch(edit.structuredAra, proposal.patch)
    : edit.structuredAra
  if (isMetaFrame(proposal)) return { sections: edit.sections, structuredAra }
  const frame = proposal as AraEditSectionFrame
  const sections = { ...edit.sections, [frame.section_key]: frame.html }
  if (frame.section_key in edit.sections && sectionOrder.includes(frame.section_key)) {
    return { sections, structuredAra }
  }
  // A key outside the order is a chat-inserted custom section: assembleBodyHtml
  // drops unlisted keys, so it must join the order — after its resolved anchor
  // (`after_section`, a section key the backend resolved from the model's
  // target id) or at the end.
  const nextOrder = sectionOrder.includes(frame.section_key)
    ? sectionOrder
    : (() => {
        const anchor = frame.after_section
        const at =
          anchor && sectionOrder.includes(anchor)
            ? sectionOrder.indexOf(anchor) + 1
            : sectionOrder.length
        return [...sectionOrder.slice(0, at), frame.section_key, ...sectionOrder.slice(at)]
      })()
  return { sections, structuredAra, sectionOrder: nextOrder }
}

function setRemoveProposalStatus(
  edit: AraVariantEditState,
  messageId: string,
  index: number,
  status: ChatProposalStatus
): AraVariantEditState {
  return {
    ...edit,
    messages: edit.messages.map(message => {
      if (message.role !== 'assistant' || message.id !== messageId) return message
      return {
        ...message,
        removeProposals: message.removeProposals.map((proposal, proposalIndex) =>
          proposalIndex === index ? { ...proposal, status } : proposal
        )
      }
    })
  }
}

function setProposalActionStatus(
  edit: AraVariantEditState,
  action: ProposalAction,
  status: ChatProposalStatus
): AraVariantEditState {
  return {
    ...edit,
    messages: edit.messages.map(message => {
      if (message.role !== 'assistant' || message.id !== action.messageId) return message
      if (action.kind === 'section') {
        return {
          ...message,
          proposals: message.proposals.map((proposal, index) =>
            index === action.index ? { ...proposal, status } : proposal
          )
        }
      }
      if (action.kind === 'target') {
        return {
          ...message,
          targetedProposals: message.targetedProposals.map((proposal, index) =>
            index === action.index ? { ...proposal, status } : proposal
          )
        }
      }
      if (action.kind === 'span') {
        return {
          ...message,
          spanProposals: message.spanProposals.map((proposal, index) =>
            index === action.index ? { ...proposal, status } : proposal
          )
        }
      }
      if (action.kind === 'remove') {
        return {
          ...message,
          removeProposals: (message.removeProposals ?? []).map((proposal, index) =>
            index === action.index ? { ...proposal, status } : proposal
          )
        }
      }
      return {
        ...message,
        chartProposals: message.chartProposals.map((proposal, index) =>
          index === action.index ? { ...proposal, status } : proposal
        )
      }
    })
  }
}

function pendingPreviewActions(edit: AraVariantEditState): ProposalAction[] {
  const actions: ProposalAction[] = []
  for (const message of edit.messages) {
    if (message.role !== 'assistant') continue
    for (const [index, proposal] of (message.proposals ?? []).entries()) {
      if (proposal.status === 'previewing') {
        actions.push({ kind: 'section', messageId: message.id, index, proposal: proposal.frame })
      }
    }
    for (const [index, proposal] of (message.targetedProposals ?? []).entries()) {
      if (proposal.status === 'previewing') {
        actions.push({ kind: 'target', messageId: message.id, index, proposal })
      }
    }
    for (const [index, proposal] of (message.spanProposals ?? []).entries()) {
      if (proposal.status === 'previewing') {
        actions.push({ kind: 'span', messageId: message.id, index, proposal })
      }
    }
    for (const [index, proposal] of (message.chartProposals ?? []).entries()) {
      if (proposal.status === 'previewing') {
        actions.push({ kind: 'chart', messageId: message.id, index, proposal })
      }
    }
    for (const [index, proposal] of (message.removeProposals ?? []).entries()) {
      if (proposal.status === 'previewing') {
        actions.push({ kind: 'remove', messageId: message.id, index, proposal })
      }
    }
  }
  return actions
}

function proposalActionStatus(
  edit: AraVariantEditState,
  action: ProposalAction
): ChatProposalStatus | undefined {
  const message = edit.messages.find(
    candidate => candidate.role === 'assistant' && candidate.id === action.messageId
  )
  if (!message || message.role !== 'assistant') return undefined
  if (action.kind === 'section') return message.proposals[action.index]?.status
  if (action.kind === 'target') return message.targetedProposals[action.index]?.status
  if (action.kind === 'span') return message.spanProposals[action.index]?.status
  if (action.kind === 'remove') return message.removeProposals?.[action.index]?.status
  return message.chartProposals[action.index]?.status
}

/** The section a proposal touched, for the activity log's `section_key`. */
function proposalSectionKey(action: ProposalAction): string | null {
  if (action.kind === 'section') return action.proposal.section_key
  if (action.kind === 'target' || action.kind === 'span') {
    return action.proposal.target?.sectionKey ?? action.proposal.targetKey ?? null
  }
  if (action.kind === 'chart') return action.proposal.target?.sectionKey ?? action.proposal.chartKey
  return action.proposal.frame.section_key
}

/** The chat transcript so far, in the shape `/ara/edit/accept` logs. */
function conversationForLog(edit: AraVariantEditState): AraEditChatMessage[] {
  return edit.messages.map(message => ({
    role: message.role,
    content:
      message.role === 'user'
        ? (message as ChatUserMessage).content
        : (message as ChatAssistantMessage).response
  }))
}

function applyProposalAction(
  edit: AraVariantEditState,
  sectionOrder: string[],
  action: ProposalAction
): AraVariantEditState {
  if (action.kind === 'section') {
    // A prior accept in the same batch may have already inserted a custom key —
    // its order outranks the draft's.
    return {
      ...edit,
      ...sectionProposalNextState(edit, action.proposal, edit.sectionOrder ?? sectionOrder)
    }
  }
  if (action.kind === 'target') {
    return {
      ...edit,
      ...applyTargetProposalToState(edit, sectionOrder, action.proposal)
    }
  }
  if (action.kind === 'span') {
    return {
      ...edit,
      ...applySpanProposalToState(edit, sectionOrder, action.proposal)
    }
  }
  if (action.kind === 'remove') {
    const outcome = applyRemoveIntentToState(
      edit,
      removeIntentFromFrame(action.proposal.frame),
      edit.sectionOrder ?? sectionOrder
    )
    if (!outcome.ok) return edit
    return {
      ...edit,
      sections: outcome.sections,
      structuredAra: outcome.structuredAra,
      sectionOrder: outcome.sectionOrder ?? edit.sectionOrder
    }
  }
  return applyChartProposalToState(edit, action.proposal)
}

function hasPendingPreview(edit: AraVariantEditState): boolean {
  return pendingPreviewActions(edit).length > 0
}

function applyActivePreviewActions(
  edit: AraVariantEditState,
  sectionOrder: string[]
): AraVariantEditState {
  return pendingPreviewActions(edit).reduce<AraVariantEditState>(
    (preview, action) => applyProposalAction(preview, sectionOrder, action),
    { ...edit, previewHtml: null }
  )
}

export function AraSessionsProvider({
  children,
  db: dbProp,
  persistDebounceMs = ARA_PERSIST_DEBOUNCE_MS,
  syncTimeoutMs = ARA_SYNC_TIMEOUT_MS
}: AraSessionsProviderProps) {
  const { t, i18n } = useTranslation('araGeneration')
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const dbRef = useRef<AraWorkspaceDb>(dbProp ?? createIndexedDbWorkspaceDb())
  const [workspaces, setWorkspaces] = useState<Record<string, AraWorkspace>>({})
  const [hydrated, setHydrated] = useState(false)
  const [readyAssessments, setReadyAssessments] = useState<Record<string, true>>({})
  const [saveStates, setSaveStates] = useState<Record<string, AraSaveState>>({})
  const workspacesRef = useRef(workspaces)
  const readyAssessmentsRef = useRef(readyAssessments)
  const saveStatesRef = useRef(saveStates)
  const generationControllers = useRef(new Map<string, AbortController>())
  const editControllers = useRef(new Map<string, AbortController>())
  // Sector-rebuild offers already decided, keyed `${assessmentId}::${variantId}::${messageId}`.
  // The message's own `status` cannot carry this alone: `commitWorkspace` mirrors
  // `workspacesRef` from inside the setState updater, so two clicks landing in the same React
  // batch would both read `pending` and start two three-minute runs, the second aborting the
  // first. This records the decision synchronously, before any state update is queued.
  const decidedSectorRebuilds = useRef(new Set<string>())
  const editBuffers = useRef(new Map<string, AraEditBuffer>())
  // Each stack entry is one undo action: a single-element removal pushes a
  // one-snapshot entry, a batch removal pushes every affected section's
  // pre-batch snapshot (FE-6248) so the whole batch rewinds together.
  const removalUndoStacks = useRef(new Map<string, SectionState[][]>())
  // One in-flight sync per assessment, chained: a new sync queues behind the
  // previous one so a slow stale PUT can never land after a newer one.
  const serverSyncChains = useRef(new Map<string, Promise<boolean>>())
  // Content version per variant key, bumped on every dirty-marking change. A
  // sync clears `unsyncedEdits` only when the version it READ is still
  // current after its PUT lands — never for edits it didn't ship.
  const serverSyncDirtyVersion = useRef(new Map<string, number>())
  const locationRef = useRef(`${location.pathname}${location.search}`)
  const navigateRef = useRef(navigate)
  const languageRef = useRef(i18n.language)
  const persistDebounceMsRef = useRef(persistDebounceMs)
  const syncTimeoutMsRef = useRef(syncTimeoutMs)
  // Late async completions (a batch whose network leg outlives the mount)
  // must not write storage or save state on behalf of a dead provider.
  const mountedRef = useRef(true)
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const persistIdleHandle = useRef<number | null>(null)
  const pendingPersist = useRef(new Set<string>())

  workspacesRef.current = workspaces
  readyAssessmentsRef.current = readyAssessments
  saveStatesRef.current = saveStates
  locationRef.current = `${location.pathname}${location.search}`
  navigateRef.current = navigate
  languageRef.current = i18n.language
  persistDebounceMsRef.current = persistDebounceMs
  syncTimeoutMsRef.current = syncTimeoutMs
  if (dbProp) dbRef.current = dbProp

  useEffect(() => {
    // StrictMode dev setup→cleanup→setup: without re-arming here, the
    // simulated cleanup leaves mountedRef=false FOREVER and every
    // persistWorkspace early-returns — no sync, no requests, spinner pinned.
    mountedRef.current = true
    let cancelled = false
    void (async () => {
      try {
        // Warm the store so first open is cheap; concrete loads happen on demand.
        await dbRef.current.getWorkspace('__warmup__')
      } catch {
        // IndexedDB may be unavailable (private mode); stay memory-only.
      } finally {
        if (!cancelled) setHydrated(true)
      }
    })()
    return () => {
      cancelled = true
      mountedRef.current = false
      cancelPersistSchedule()
    }
  }, [])

  function markWorkspaceReady(assessmentId: string) {
    if (readyAssessmentsRef.current[assessmentId]) return
    setReadyAssessments(current => {
      if (current[assessmentId]) return current
      const next = { ...current, [assessmentId]: true as const }
      readyAssessmentsRef.current = next
      return next
    })
  }

  /**
   * Local persistence scheduling (ADR 0001: off the interaction hot path).
   * Edits land in `pendingPersist`; a trailing debounce re-armed by every
   * change means a typing burst never performs work itself. When the debounce
   * fires, the batch runs at the next idle slot and writes the workspace to
   * IndexedDB. This local crash-protection write is separate from server sync
   * state and must not make the header say "Unsaved changes".
   */
  function schedulePersist(assessmentId: string) {
    pendingPersist.current.add(assessmentId)
    if (persistTimer.current) clearTimeout(persistTimer.current)
    persistTimer.current = setTimeout(runPersistBatchWhenIdle, persistDebounceMsRef.current)
  }

  function cancelPersistSchedule() {
    if (persistTimer.current) {
      clearTimeout(persistTimer.current)
      persistTimer.current = null
    }
    if (
      persistIdleHandle.current !== null &&
      typeof window !== 'undefined' &&
      typeof window.cancelIdleCallback === 'function'
    ) {
      window.cancelIdleCallback(persistIdleHandle.current)
      persistIdleHandle.current = null
    }
  }

  function runPersistBatchWhenIdle() {
    persistTimer.current = null
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      persistIdleHandle.current = window.requestIdleCallback(
        () => {
          persistIdleHandle.current = null
          runPersistBatch()
        },
        { timeout: ARA_PERSIST_IDLE_TIMEOUT_MS }
      )
      return
    }
    runPersistBatch()
  }

  function runPersistBatch() {
    const ids = [...pendingPersist.current]
    pendingPersist.current.clear()
    // The debounced batch is local-only crash protection. Server sync remains
    // explicit (Save / publish / open) and is tracked separately.
    for (const id of ids) void persistWorkspaceLocal(id)
  }

  /** Immediate flush, bypassing debounce and idle wait (visibilitychange/beforeunload). */
  function flushPendingPersist() {
    cancelPersistSchedule()
    runPersistBatch()
  }

  function transitionSaveState(
    assessmentId: string,
    status: AraSaveState['status'],
    lastSavedAt?: number
  ) {
    setSaveStates(current => {
      const previous = current[assessmentId] ?? IDLE_SAVE_STATE
      // Guard against per-keystroke renders: repeated dirty marks while the
      // workspace is already pending must be a no-op.
      if (previous.status === status && lastSavedAt === undefined) return current
      const next = {
        ...current,
        [assessmentId]: {
          status,
          lastSavedAt: lastSavedAt ?? previous.lastSavedAt
        }
      }
      saveStatesRef.current = next
      return next
    })
  }

  /**
   * Apply buffered edits and write the local cache (IndexedDB). NO server
   * sync — this is the crash-protection sink behind the autosave batch and
   * the window-boundary flush.
   */
  async function persistWorkspaceLocal(assessmentId: string): Promise<boolean> {
    if (!mountedRef.current) return false
    applyEditBuffers(assessmentId)
    const workspace = workspacesRef.current[assessmentId]
    try {
      if (!workspace) {
        await dbRef.current.deleteWorkspace(assessmentId)
      } else {
        await dbRef.current.putWorkspace(sanitizeWorkspaceForPersistence(workspace))
      }
      return true
    } catch {
      return false
    }
  }

  /**
   * The explicit sync pipeline: local cache + the bounded, serialized server
   * sync. Only called from explicit acts — the Save button, the pre-open
   * flush, the publish preflight, and the once-per-variant generation birth.
   */
  async function persistWorkspace(assessmentId: string) {
    transitionSaveState(assessmentId, 'saving')
    // Local write first (crash cache)…
    await persistWorkspaceLocal(assessmentId)

    // Bounded + serialized (see enqueueServerSync): a hung request is
    // aborted at the sync bound — surfacing 'error' with the marker
    // retained for the next explicit save — and a slow batch can never
    // leapfrog a newer PUT with a stale one.
    const serverOk = await enqueueServerSync(assessmentId).catch(() => false)

    // …and a second best-effort local write settles the unsyncedEdits flag the
    // sync cleared (or keeps it set when the sync failed).
    await persistWorkspaceLocal(assessmentId)

    // The header describes server sync, not the IndexedDB crash cache. A
    // successful server sync settles it even if a local cache write failed.
    const hasUnsyncedEdits = workspacesRef.current[assessmentId]?.variants.some(
      variant =>
        variant.unsyncedEdits === true ||
        editBuffers.current.has(controllerKey(assessmentId, variant.id))
    )
    if (serverOk && hasUnsyncedEdits) {
      transitionSaveState(assessmentId, 'pending')
    } else if (serverOk) {
      transitionSaveState(assessmentId, 'saved', Date.now())
    } else {
      transitionSaveState(assessmentId, 'error')
    }
  }

  /**
   * Explicit Save: cancel the pending schedule and run the batch for this
   * assessment immediately. No publish semantics — the draft stays open.
   */
  async function saveNow(assessmentId: string): Promise<void> {
    if (persistTimer.current) {
      clearTimeout(persistTimer.current)
      persistTimer.current = null
    }
    pendingPersist.current.delete(assessmentId)
    await persistWorkspace(assessmentId)
  }

  /**
   * Creates the server-side draft document for a completed generation (born
   * at phase done: aborted/failed generations never reach this). Returns the
   * document id, or null on failure — the caller treats null as "stay local,
   * retry next batch" so an unreachable API never blocks editing.
   */
  async function createServerDraft(
    assessmentId: string,
    variantId: string,
    signal?: AbortSignal
  ): Promise<string | null> {
    let variant = findVariant(workspacesRef.current, assessmentId, variantId)
    if (!variant?.draftPayload) return null
    if (variant.serverDocumentId) return variant.serverDocumentId
    try {
      const { id } = await createAraDraftDocument({
        plantId: assessmentId,
        title: `ARA — ${variant.name}`,
        signal
      })
      commitWorkspace(
        assessmentId,
        workspace =>
          mapVariant(workspace, variantId, current => ({
            ...current,
            serverDocumentId: id
          })),
        { persist: false }
      )
      variant = findVariant(workspacesRef.current, assessmentId, variantId)
      if (variant?.serverDocumentId !== id) {
        // The deferred-updater race strikes exactly here (the sync loop reads
        // the id synchronously): mirror the ref like applyEditBuffers does.
        const workspace = workspacesRef.current[assessmentId]
        if (workspace) {
          workspacesRef.current = {
            ...workspacesRef.current,
            [assessmentId]: mapVariant(workspace, variantId, current => ({
              ...current,
              serverDocumentId: id
            }))
          }
        }
      }
      markServerSyncDirty(assessmentId, variantId)
      // The draft appears in the assessment's documents list with no manual
      // action (sidebar merge surfaces it as a server document).
      void queryClient.invalidateQueries({ queryKey: documentsKeys.list(assessmentId) })
      return id
    } catch {
      return null
    }
  }

  /**
   * Set the persisted unsynced-edits flag AND mirror the ref synchronously.
   * updateVariant's ref mirror lives inside the React updater (render time),
   * but the flag must be visible to the SAME tick — the IDB write right
   * after buffering, the sync cleanup right after a landed PUT — or the
   * persisted truth would lag one render and be lost on reload. The update
   * is a pure set: re-applying to the ref is result-identical.
   */
  function setVariantUnsyncedFlag(assessmentId: string, variantId: string, unsynced: boolean) {
    const applyFlag = (variant: AraVariant): AraVariant => ({ ...variant, unsyncedEdits: unsynced })
    updateVariant(assessmentId, variantId, applyFlag, { persist: false })
    const current = workspacesRef.current[assessmentId]
    if (current) {
      workspacesRef.current = {
        ...workspacesRef.current,
        [assessmentId]: mapVariant(current, variantId, applyFlag)
      }
    }
  }

  function bumpServerSyncVersion(assessmentId: string, variantId: string) {
    const key = controllerKey(assessmentId, variantId)
    serverSyncDirtyVersion.current.set(key, (serverSyncDirtyVersion.current.get(key) ?? 0) + 1)
  }

  /**
   * Mark a variant's source as needing an explicit server sync: set the
   * persisted `unsyncedEdits` flag (survives reload/crash — the reopen
   * guard's source of truth) and advance its content version.
   */
  function markServerSyncDirty(assessmentId: string, variantId: string) {
    bumpServerSyncVersion(assessmentId, variantId)
    setVariantUnsyncedFlag(assessmentId, variantId, true)
    transitionSaveState(assessmentId, 'pending')
  }

  /**
   * Mark buffered content as server-unsynced without committing the workspace
   * to React state on every keystroke. The batch persists the flag with the
   * buffered content after it applies them.
   */
  function markBufferedServerSyncDirty(assessmentId: string, variantId: string) {
    bumpServerSyncVersion(assessmentId, variantId)
    transitionSaveState(assessmentId, 'pending')
  }

  /**
   * Serialize the server half of the pipeline per assessment: the next sync
   * queues behind the currently in-flight one (success OR failure), so PUTs
   * never overlap. Each entry's network legs carry an AbortController that
   * the sync bound cancels when it fires — the bound starts when the entry
   * STARTS (not at enqueue), so queued entries are never preemptively
   * aborted and a timed-out entry always settles: a hung leg can never
   * poison the chain, and an aborted PUT can never land late and overwrite
   * a newer source with stale content.
   */
  function enqueueServerSync(assessmentId: string): Promise<boolean> {
    const previous = serverSyncChains.current.get(assessmentId)
    const next = (previous ?? Promise.resolve(true))
      .catch(() => false)
      .then(() => {
        // Two layers of the same bound. The abort cancels a request that
        // already dispatched (axios honors the signal, and the cancelled PUT
        // can never land late). The raced rejection severs an entry stalled
        // BEFORE dispatch — the axios request interceptor may await the auth
        // token indefinitely, where no signal listener is attached yet —
        // so the entry ALWAYS settles and never poisons the chain.
        const controller = new AbortController()
        const abortTimer = setTimeout(() => controller.abort(), syncTimeoutMsRef.current)
        const leg = syncServerDrafts(assessmentId, controller.signal).finally(() =>
          clearTimeout(abortTimer)
        )
        return withSyncTimeout(leg, syncTimeoutMsRef.current).catch(() => false)
      })
    serverSyncChains.current.set(assessmentId, next)
    return next
  }

  /**
   * Server half of an explicit save: for every completed variant, ensure its
   * draft document exists and, when `unsyncedEdits` is set, PUT the current
   * source (create-or-replace).
   */
  async function syncServerDrafts(assessmentId: string, signal?: AbortSignal): Promise<boolean> {
    const workspace = workspacesRef.current[assessmentId]
    if (!workspace) return true
    let allSynced = true
    for (const variant of workspace.variants) {
      if (variant.phase !== 'done' || !variant.draftPayload) continue
      const key = controllerKey(assessmentId, variant.id)
      const documentId =
        variant.serverDocumentId ?? (await createServerDraft(assessmentId, variant.id, signal))
      if (!documentId) {
        allSynced = false
        continue
      }
      const latest = findVariant(workspacesRef.current, assessmentId, variant.id) ?? variant
      if (latest.unsyncedEdits !== true) continue
      try {
        const versionAtRead = serverSyncDirtyVersion.current.get(key) ?? 0
        const payload = buildAraSourcePayload(latest)
        if (payload) {
          const contentBase64 = encodeAraSource(payload)
          await putDocumentSource({ plantId: assessmentId, documentId, contentBase64, signal })
        }
        if ((serverSyncDirtyVersion.current.get(key) ?? 0) === versionAtRead) {
          setVariantUnsyncedFlag(assessmentId, variant.id, false)
        }
      } catch {
        allSynced = false
      }
    }
    return allSynced
  }

  /**
   * Opens a kind=ara server document in the editor: downloads the source and
   * rehydrates (or refreshes) the variant, regardless of which browser
   * created it — but ONLY when the local copy is provably synced. The
   * IndexedDB workspace stays as the write-through cache; published
   * documents rehydrate read-only; status changes are NOT possible through
   * this surface (the explicit Edit action is FE-5412). Rehydration
   * degrades to the cached local copy when the fetch/decode fails: a stale
   * editor beats none, and an error toast makes the fallback clear. With no
   * local copy (e.g. first open from another browser), the same error toast
   * reports the failed open.
   *
   * THE UNSYNCED CONTRACT: the persisted `unsyncedEdits` flag survives the
   * reload/crash that wipes the in-memory markers, so a reopen ALWAYS knows
   * when local content is ahead of the server source. While dirty, the
   * reopen flushes (explicit sync) and keeps the LOCAL copy either way —
   * it never rebuilds state from a server source the edits never reached
   * (the "only the first edit of the session persists" bug).
   */
  async function openServerDocument(
    assessmentId: string,
    document: AraServerDocumentRef
  ): Promise<void> {
    const workspace = await hydrateWorkspace(assessmentId)
    const existing =
      workspace.variants.find(variant => variant.serverDocumentId === document.id) ?? null

    if (existing) {
      const hasUnsynced =
        existing.unsyncedEdits === true ||
        editBuffers.current.has(controllerKey(assessmentId, existing.id))
      if (hasUnsynced) {
        // Never let an open silently discard unsynced work: flush the local
        // copy to the server first. saveNow never throws — a sync failure is
        // swallowed into saveState 'error' with markers and flag retained.
        await saveNow(assessmentId)
        // Flushed or not, the LOCAL copy is the freshest content in reach:
        // keep it (refreshing from the server now would either be a no-op —
        // same content — or a clobber of edits the PUT didn't land).
        setActiveVariant(assessmentId, existing.id)
        return
      }
    }

    let sourcePayload: AraSourcePayload
    try {
      // Bounded: a hung download must land on the same degrade path as a
      // failed one, never wedge the open with a forever-pending promise.
      const source = await withSyncTimeout(
        getDocumentSource({ plantId: assessmentId, documentId: document.id }),
        syncTimeoutMsRef.current
      )
      sourcePayload = decodeAraSource(source.contentBase64)
    } catch {
      toast.error(t('open.error'))
      if (existing) setActiveVariant(assessmentId, existing.id)
      return
    }

    // sections + structuredAra are the source of truth; the blob's cached
    // lastRenderedHtml is a cosmetic cache that can be stale FOREVER (a
    // failed preview refresh is persist:false'd, so no later batch ever
    // heals it). Re-render from the truth on open so the editor — and the
    // read-only published view — always shows the synced content. A failed
    // or hung re-render degrades to the cached snapshot: stale beats blank.
    let lastRenderedHtml = sourcePayload.lastRenderedHtml ?? ''
    try {
      const rendered = await withSyncTimeout(
        renderAra({
          structured_ara: sourcePayload.structuredAra,
          body_html: assembleBodyHtml(sourcePayload.sections, sourcePayload.sectionOrder)
        }),
        syncTimeoutMsRef.current
      )
      // A degenerate (empty) render must not clobber the cached snapshot.
      if (rendered) lastRenderedHtml = rendered
    } catch {
      // keep the cached snapshot
    }

    const readOnly = document.status === 'published'
    const variantId = existing?.id ?? createVariantId()
    removalUndoStacks.current.delete(controllerKey(assessmentId, variantId))
    const draftPayload: AraDraftPayload = {
      assessmentId,
      variantId,
      sections: { ...sourcePayload.sections },
      structuredAra: sourcePayload.structuredAra,
      lastRenderedHtml,
      sectionOrder: [...sourcePayload.sectionOrder],
      customSections: sourcePayload.customSections.map(section => ({ ...section }))
    }
    const editState: AraVariantEditState = {
      messages: [],
      sections: { ...sourcePayload.sections },
      structuredAra: sourcePayload.structuredAra,
      lastRenderedHtml: draftPayload.lastRenderedHtml,
      streaming: false,
      focusedSectionKey: null,
      selectedElementTarget: null,
      previewHtml: null
    }

    commitWorkspace(assessmentId, current => {
      if (existing) {
        return {
          ...mapVariant(current, variantId, variant => ({
            ...variant,
            phase: 'done' as const,
            readOnly,
            draftPayload,
            edit: editState,
            configure: sourcePayload.configure ?? variant.configure,
            scopeSavedAt: sourcePayload.scopeSavedAt ?? variant.scopeSavedAt ?? null
          })),
          activeVariantId: variantId,
          showConfigure: false
        }
      }
      return {
        ...current,
        showConfigure: false,
        activeVariantId: variantId,
        variants: [
          ...current.variants,
          {
            ...emptyVariant({ id: variantId, name: document.title }),
            phase: 'done' as const,
            draftPayload,
            edit: editState,
            serverDocumentId: document.id,
            readOnly,
            configure: sourcePayload.configure ?? null,
            scopeSavedAt: sourcePayload.scopeSavedAt ?? null
          }
        ]
      }
    })
  }

  function ensureEditBuffer(assessmentId: string, variantId: string): AraEditBuffer {
    const key = controllerKey(assessmentId, variantId)
    let buffer = editBuffers.current.get(key)
    if (!buffer) {
      buffer = { textOverrides: {}, sectionBodies: {} }
      editBuffers.current.set(key, buffer)
    }
    return buffer
  }

  /**
   * Single state application for everything the burst buffered: text overrides
   * merge into structuredAra, inline section bodies merge back into their
   * wrapped sections. The merge is computed synchronously from the ref (not
   * via a React updater) so same-tick readers — the IDB write right below, a
   * proposal render, the publish render — see the applied content, and the
   * commit is a plain replace (safe: read and commit happen in one tick).
   * Text edits also trigger ONE trailing preview re-render (the per-keystroke
   * renderAra call this replaces was the jank source); callers about to render
   * themselves pass refreshPreview: false to skip the obsolete render.
   */
  function applyEditBuffers(assessmentId: string, options?: { refreshPreview?: boolean }) {
    const refreshPreview = options?.refreshPreview ?? true
    const workspace = workspacesRef.current[assessmentId]
    if (!workspace) return
    const applied: Array<{
      variantId: string
      edit: AraVariantEditState
      hasText: boolean
      previousEdit: AraVariantEditState
    }> = []
    for (const variant of workspace.variants) {
      const key = controllerKey(assessmentId, variant.id)
      const buffer = editBuffers.current.get(key)
      if (!buffer) continue
      editBuffers.current.delete(key)
      const { textOverrides, sectionBodies } = buffer
      // Chat-inserted elements carry `chat.*` edit keys (assigned by the ARA
      // normalizer). Their inline edits belong to the document HTML itself —
      // writing them into structuredAra.text_overrides would create structured
      // entries the template render never consumes. Template keys (hero, footer)
      // keep the text_overrides contract; the section mirror covers both.
      const templateOverrides: Record<string, string> = {}
      const chatOverrides: Record<string, string> = {}
      for (const [overrideKey, value] of Object.entries(textOverrides)) {
        if (overrideKey.startsWith(ARA_CHAT_EDIT_KEY_PREFIX)) chatOverrides[overrideKey] = value
        else templateOverrides[overrideKey] = value
      }
      const hasText = Object.keys(chatOverrides).length > 0 || Object.keys(templateOverrides).length > 0
      const hasSections = Object.keys(sectionBodies).length > 0
      if (!hasText && !hasSections) continue

      const edit = variant.edit
      if (!edit) continue
      let nextEdit: AraVariantEditState = edit
      if (hasText) {
        let sections = nextEdit.sections
        if (Object.keys(chatOverrides).length > 0) {
          sections = applyTextOverridesToSections(sections, chatOverrides)
        }
        if (Object.keys(templateOverrides).length > 0) {
          // structuredAra is Record<string, unknown>; text_overrides is the one
          // field we co-own with the ARA template contract (writable string map).
          const prevOverrides =
            (edit.structuredAra.text_overrides as Record<string, string> | undefined) ?? {}
          nextEdit = {
            ...nextEdit,
            structuredAra: {
              ...nextEdit.structuredAra,
              text_overrides: { ...prevOverrides, ...templateOverrides }
            },
            sections: applyTextOverridesToSections(sections, templateOverrides)
          }
        } else {
          nextEdit = { ...nextEdit, sections }
        }
      }
      if (hasSections) {
        let sections = nextEdit.sections
        for (const [sectionKey, html] of Object.entries(sectionBodies)) {
          const previous = sections[sectionKey]
          // The injected report script emits the `.ara-section-body` innerHTML
          // (body only). Merge it back into the stored wrapped `<section>` so
          // the wrapper and header survive the next render.
          sections = {
            ...sections,
            [sectionKey]: previous ? replaceSectionBody(previous, html) : html
          }
        }
        nextEdit = { ...nextEdit, sections }
      }
      applied.push({ variantId: variant.id, edit: nextEdit, hasText, previousEdit: edit })
    }
    if (applied.length === 0) return
    for (const { variantId } of applied) {
      bumpServerSyncVersion(assessmentId, variantId)
    }

    const appliedByVariant = new Map(applied.map(entry => [entry.variantId, entry.edit]))
    const merged: AraWorkspace = {
      ...workspace,
      variants: workspace.variants.map(variant => {
        const edit = appliedByVariant.get(variant.id)
        return edit ? { ...variant, edit, unsyncedEdits: true } : variant
      })
    }
    commitWorkspace(assessmentId, () => merged, { persist: false })
    workspacesRef.current = { ...workspacesRef.current, [assessmentId]: merged }
    // Record the inline (non-AI) edits this flush applied. One row per buffered
    // batch, not per keystroke — the buffer is what makes a burst of typing a
    // single logical edit. Fire-and-forget, like every other tracking call.
    for (const { variantId, edit, previousEdit } of applied) {
      const variant = merged.variants.find(candidate => candidate.id === variantId)
      // Only the sections this batch actually touched. Diffing the whole
      // assembled body would ship the entire report twice per flush — the
      // server only stores a diff, so the untouched sections are pure waste.
      const changed = Object.keys(edit.sections).filter(
        sectionKey => edit.sections[sectionKey] !== previousEdit.sections[sectionKey]
      )
      if (changed.length === 0) continue
      void araEditLog({
        assessment_id: assessmentId,
        kind: 'manual',
        section_key: changed.length === 1 ? changed[0] : null,
        before: changed.map(sectionKey => previousEdit.sections[sectionKey] ?? '').join('\n'),
        after: changed.map(sectionKey => edit.sections[sectionKey] ?? '').join('\n'),
        sector: variant?.configure?.sector ?? null,
        plant_name: araPlantName(edit.structuredAra) || null
      })
    }
    if (refreshPreview) {
      for (const { variantId, edit, hasText } of applied) {
        if (hasText) void refreshRenderedHtml(assessmentId, variantId, edit)
      }
    }
  }

  /**
   * Synchronous flush of buffered edits into state, for callers that read
   * edit content to render or send it right away (proposals, edit chat,
   * publish). This is the "until needed" half of the hot-path contract: the
   * burst stays out of React state, but nothing downstream ever acts on stale
   * content. Never triggers the trailing preview re-render — the caller's own
   * render supersedes it.
   */
  function flushEdits(assessmentId: string) {
    applyEditBuffers(assessmentId, { refreshPreview: false })
  }

  /**
   * Reset a variant's edit state to a caller-supplied baseline (the shape used
   * when the cancel-edit flow exits without publishing): abort any in-flight
   * edit stream, drop any buffered keystrokes so they cannot resurrect on the
   * next `flushEdits`, and replace the whole edit slice atomically. Local-only
   * by design — it does NOT touch `saveNow`, which would serialize the LIVE
   * edit state to the server. The caller reconciles the server source
   * separately (direct `putDocumentSource`).
   */
  function resetVariantEdit(
    assessmentId: string,
    variantId: string,
    baseline: AraVariantEditState
  ) {
    const key = controllerKey(assessmentId, variantId)
    editControllers.current.get(key)?.abort()
    editControllers.current.delete(key)
    editBuffers.current.delete(key)
    removalUndoStacks.current.delete(key)
    updateEdit(assessmentId, variantId, () => baseline)
    // The restored baseline IS the server source (the caller PUTs it as the
    // reconcile step), so the "local ahead of server" marker must clear too —
    // otherwise the next saveNow re-PUTs the same content against a published
    // document and the draft-gated endpoint 409s (FE-5502).
    setVariantUnsyncedFlag(assessmentId, variantId, false)
  }

  /**
   * Trailing preview refresh after a buffered text-edit batch. Explicitly
   * takes the applied edit instead of re-reading state, so it never races the
   * React commit. Best-effort: overrides are the source of truth and
   * publish/proposals always re-render from them.
   */
  async function refreshRenderedHtml(
    assessmentId: string,
    variantId: string,
    appliedEdit: AraVariantEditState
  ) {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    if (!draft) return
    const controllerId = controllerKey(assessmentId, variantId)
    editControllers.current.get(controllerId)?.abort()
    const controller = new AbortController()
    editControllers.current.set(controllerId, controller)
    try {
      const renderedHtml = await renderAra(
        {
          structured_ara: appliedEdit.structuredAra,
          body_html: assembleBodyHtml(appliedEdit.sections, draft.sectionOrder)
        },
        controller.signal
      )
      if (controller.signal.aborted) return
      // persist:false — the rendered snapshot is a cosmetic cache: persisting it
      // must not re-arm a batch behind the user's back (and behind the one that
      // just ran). The next real commit writes it along.
      updateEdit(
        assessmentId,
        variantId,
        current => ({
          ...current,
          lastRenderedHtml: renderedHtml,
          echoRender: { base: current.lastRenderedHtml, html: renderedHtml }
        }),
        { persist: false }
      )
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') return
      // Preview refresh failure leaves lastRenderedHtml stale; next batch retries.
    } finally {
      if (editControllers.current.get(controllerId) === controller) {
        editControllers.current.delete(controllerId)
      }
    }
  }

  function ensureWorkspace(assessmentId: string): AraWorkspace {
    return workspacesRef.current[assessmentId] ?? emptyWorkspace(assessmentId)
  }

  function commitWorkspace(
    assessmentId: string,
    update: (workspace: AraWorkspace) => AraWorkspace,
    options?: { persist?: boolean }
  ) {
    setWorkspaces(current => {
      const previous = current[assessmentId] ?? emptyWorkspace(assessmentId)
      const nextWorkspace = update(previous)
      const next = { ...current, [assessmentId]: nextWorkspace }
      workspacesRef.current = next
      return next
    })
    if (options?.persist !== false) schedulePersist(assessmentId)
  }

  /**
   * Load the persisted workspace and RETURN it (with the ref mirrored
   * synchronously). Previously the ref was only mirrored inside the React
   * updater, so a same-tick reader (openServerDocument's existing-variant
   * lookup right after `await hydrateWorkspace`) ran against the empty
   * pre-hydration state — misreading the variant as "not opened here
   * before" and creating a duplicate variant from the stale server source.
   */
  async function hydrateWorkspace(assessmentId: string): Promise<AraWorkspace> {
    const current = workspacesRef.current[assessmentId]
    if (current) {
      markWorkspaceReady(assessmentId)
      return current
    }
    try {
      const stored = await dbRef.current.getWorkspace(assessmentId)
      if (stored && !workspacesRef.current[assessmentId]) {
        const normalized = normalizeWorkspace(stored, languageRef.current)
        workspacesRef.current = { ...workspacesRef.current, [assessmentId]: normalized }
        setWorkspaces(current => {
          if (current[assessmentId]) return current
          return { ...current, [assessmentId]: normalized }
        })
      }
    } catch {
      // leave empty
    }
    markWorkspaceReady(assessmentId)
    return workspacesRef.current[assessmentId] ?? emptyWorkspace(assessmentId)
  }

  useEffect(() => {
    const match = location.pathname.match(/\/plant-assessment\/([^/?#]+)/)
    if (!match) return
    if (!new URLSearchParams(location.search).has('ara')) return
    void hydrateWorkspace(match[1])
  }, [location.pathname, location.search, hydrated])

  // Flush in-flight edits at the window boundary so quick edits are never lost
  // to a tab switch, minimize, or close. beforeunload/pagehide cannot await, so
  // the put is fire-and-forget — IndexedDB transactions initiated here still
  // commit during unload.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushPendingPersist()
    }
    const handleBeforeUnload = () => flushPendingPersist()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  function isViewingAra(assessmentId: string): boolean {
    const current = locationRef.current
    const path = current.split('?')[0] ?? current
    const search = current.includes('?') ? current.slice(current.indexOf('?') + 1) : ''
    const params = new URLSearchParams(search)
    return path === paths.assessmentDetail(assessmentId) && params.has('ara')
  }

  function notify(assessmentId: string, kind: 'success' | 'error', messageKey: string) {
    if (isViewingAra(assessmentId)) return
    toast[kind](t(messageKey, { assessmentId }), {
      action: {
        label: t('background.open'),
        onClick: () => void navigateRef.current(paths.assessmentDocumentsAra(assessmentId))
      }
    })
  }

  function updateVariant(
    assessmentId: string,
    variantId: string,
    update: (variant: AraVariant) => AraVariant,
    options?: { persist?: boolean }
  ) {
    commitWorkspace(assessmentId, workspace => mapVariant(workspace, variantId, update), options)
  }

  async function runGeneration(
    assessmentId: string,
    variantId: string,
    configure: AraConfigurePayload
  ): Promise<void> {
    const key = controllerKey(assessmentId, variantId)
    generationControllers.current.get(key)?.abort()
    const controller = new AbortController()
    generationControllers.current.set(key, controller)
    const ownedConfigure =
      normalizeConfigureOnLoad(configure, languageRef.current) ?? cloneConfigure(configure)
    // A run discards the variant's edit state wholesale, so buffered-but-not-yet-applied
    // keystrokes must die with it. Left alive they are applied by the next idle batch
    // (or a still-running saveNow chain) onto the fresh run — resurrecting both the edit
    // and, via applyEditBuffers' captured-replace commit, the PRE-run workspace snapshot
    // (configure included) over the new state.
    editBuffers.current.delete(key)
    const orderedSections = requestedSections(ownedConfigure)
    const allowedSectionKeys = new Set(orderedSections.map(section => section.key))

    updateVariant(assessmentId, variantId, variant => ({
      ...variant,
      phase: 'streaming',
      stage: 'preparing',
      sectionProgress: {},
      requestedSections: orderedSections,
      backboneStatus: '',
      backboneStageCount: 0,
      error: null,
      configure: ownedConfigure,
      draftPayload: null,
      edit: null,
      scopeSavedAt: Date.now()
    }))

    const request: AraGenerateRequest = toMasterAraStreamRequest({
      assessment_id: assessmentId,
      roles: ['se'],
      sections: ownedConfigure.sections,
      section_order: ownedConfigure.sectionOrder,
      custom_sections:
        ownedConfigure.customSections.length > 0 ? ownedConfigure.customSections : null,
      style_guide_extra: ownedConfigure.styleGuideExtra?.trim() || null,
      section_guidance_extra: sanitizeSectionGuidanceExtra(ownedConfigure.sectionGuidanceExtra),
      language: ownedConfigure.language,
      currency: ownedConfigure.currency,
      // Null is the normal case and means "resolve it from the HubSpot deal". A value here
      // is an SE override, which outranks every other tier — the only way to correct a CRM
      // record that is simply wrong about what the plant makes.
      sector: ownedConfigure.sector ?? null,
      // Asset scope (SCP-127): the field is serialized only when a non-empty selection
      // exists, so unscoped payloads keep the exact wire shape older backends expect.
      // An empty array is the blocked-at-the-form "nothing selected" state and never runs.
      included_asset_ids:
        ownedConfigure.includedAssetIds && ownedConfigure.includedAssetIds.length > 0
          ? ownedConfigure.includedAssetIds
          : null,
      attachments: ownedConfigure.attachments.length > 0 ? ownedConfigure.attachments : null
    })
    const collectedSections: Record<string, string> = {}
    let roleHtml = ''
    let structuredAra: Record<string, unknown> = {}
    let streamError: string | null = null
    let resolvedSectionOrder = orderedSections.map(section => section.key)

    try {
      for await (const frame of streamAra(request, controller.signal)) {
        if (controller.signal.aborted) break
        if (frame.type === 'init') {
          const nextRequested = requestedSectionsFromInit(frame.sections, orderedSections)
          resolvedSectionOrder = nextRequested.map(section => section.key)
          allowedSectionKeys.clear()
          for (const section of nextRequested) allowedSectionKeys.add(section.key)
          updateVariant(assessmentId, variantId, variant => ({
            ...variant,
            requestedSections: nextRequested
          }))
        } else if (frame.type === 'node') {
          updateVariant(assessmentId, variantId, variant => ({
            ...variant,
            backboneStatus: frame.label,
            backboneStageCount: variant.backboneStageCount + 1,
            stage:
              variant.requestedSections.length > 0 && variant.backboneStageCount + 1 >= 5
                ? 'generating'
                : variant.stage
          }))
        } else if (frame.type === 'section') {
          if (allowedSectionKeys.size > 0 && !allowedSectionKeys.has(frame.key)) {
            continue
          }
          collectedSections[frame.key] = frame.html ?? frame.md ?? ''
          updateVariant(assessmentId, variantId, variant => {
            if (
              variant.requestedSections.length > 0 &&
              !variant.requestedSections.some(section => section.key === frame.key)
            ) {
              return variant
            }
            const sectionProgress = {
              ...variant.sectionProgress,
              [frame.key]: {
                status: frame.status,
                elapsed_ms: frame.elapsed_ms,
                chars: frame.chars
              }
            }
            const completeCount = Object.values(sectionProgress).filter(
              progress => progress.status === 'complete'
            ).length
            const total = Math.max(variant.requestedSections.length, 1)
            return {
              ...variant,
              stage: completeCount >= total ? 'finalizing' : 'generating',
              sectionProgress
            }
          })
        } else if (frame.type === 'role') {
          if (frame.html) roleHtml = frame.html
        } else if (frame.type === 'done') {
          if (frame.error) streamError = frame.error
          else if (frame.structured_ara && typeof frame.structured_ara === 'object') {
            structuredAra = frame.structured_ara
          }
        } else if (frame.type === 'error') {
          streamError = frame.detail
        }
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        streamError = error instanceof Error ? error.message : String(error)
      }
    }

    if (controller.signal.aborted) return
    if (streamError) {
      updateVariant(assessmentId, variantId, variant => ({
        ...variant,
        phase: 'error',
        error: streamError
      }))
      notify(assessmentId, 'error', 'background.generationFailed')
      generationControllers.current.delete(key)
      return
    }

    updateVariant(assessmentId, variantId, variant => ({ ...variant, stage: 'finalizing' }))
    const fallbackOrder =
      resolvedSectionOrder.length > 0 ? resolvedSectionOrder : ownedConfigure.sectionOrder
    // The generation stream emits each section RAW (no `.ara-section` wrapper),
    // which leaves the report unselectable/uneditable. The `role` frame carries
    // the assembled, already-wrapped body — split it back into the sections map
    // so picker, splice, and section-scoped edits all resolve. Fall back to the
    // raw concat only when the role frame is absent (assemble error).
    const split = roleHtml ? splitRoleHtmlIntoSections(roleHtml) : null
    const sectionsSnapshot =
      split && split.order.length > 0 ? split.sections : { ...collectedSections }
    const sectionOrder = split && split.order.length > 0 ? split.order : fallbackOrder
    const bodyHtml = assembleBodyHtml(sectionsSnapshot, sectionOrder)

    try {
      const renderedHtml = await renderAra(
        { structured_ara: structuredAra, body_html: bodyHtml },
        controller.signal
      )
      const draftPayload: AraDraftPayload = {
        assessmentId,
        variantId,
        sections: sectionsSnapshot,
        structuredAra,
        lastRenderedHtml: renderedHtml,
        sectionOrder,
        customSections: ownedConfigure.customSections.map(section => ({ ...section }))
      }
      const applyGenerationDone = (workspace: AraWorkspace) =>
        mapVariant(
          {
            ...workspace,
            draftIntake: null
          },
          variantId,
          variant => ({
            ...variant,
            phase: 'done' as const,
            draftPayload,
            edit: {
              messages: [],
              sections: { ...sectionsSnapshot },
              structuredAra,
              lastRenderedHtml: renderedHtml,
              streaming: false,
              focusedSectionKey: null,
              selectedElementTarget: null,
              previewHtml: null
            }
          })
        )
      commitWorkspace(assessmentId, applyGenerationDone)
      // The done-commit reaches workspacesRef only when React renders (the
      // mirror lives inside the updater), but the birth saveNow below reads
      // it within microtasks — mirror the ref now, the same pattern
      // applyEditBuffers uses for same-tick readers.
      const latestWorkspace = workspacesRef.current[assessmentId]
      if (latestWorkspace) {
        workspacesRef.current = {
          ...workspacesRef.current,
          [assessmentId]: applyGenerationDone(latestWorkspace)
        }
      }
      // The draft is born server-side at completion (FE-5410): birth is a
      // one-time system act (NOT an edit-save), so it fires the explicit
      // sync itself — creating the document and uploading the initial source
      // such that the ARA is openable from any browser. User EDITS after
      // this point sync only on explicit Save.
      markServerSyncDirty(assessmentId, variantId)
      void saveNow(assessmentId)
      notify(assessmentId, 'success', 'background.generationComplete')
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') return
      const message = error instanceof Error ? error.message : String(error)
      updateVariant(assessmentId, variantId, variant => ({
        ...variant,
        phase: 'error',
        error: message
      }))
      notify(assessmentId, 'error', 'background.generationFailed')
    } finally {
      generationControllers.current.delete(key)
    }
  }

  async function startGeneration(
    assessmentId: string,
    configure: AraConfigurePayload,
    options?: StartGenerationOptions
  ): Promise<string> {
    await hydrateWorkspace(assessmentId)
    const variantId = createVariantId()
    const ownedConfigure = cloneConfigure(configure)
    const ownedBrief = cloneBrief(options?.brief)
    const name = nextVariantName(ensureWorkspace(assessmentId).variants)

    commitWorkspace(assessmentId, workspace => ({
      ...workspace,
      lastConfigure: cloneConfigure(ownedConfigure),
      showConfigure: false,
      activeVariantId: variantId,
      variants: [
        ...workspace.variants,
        emptyVariant({
          id: variantId,
          name,
          configure: ownedConfigure,
          brief: ownedBrief
        })
      ]
    }))

    void runGeneration(assessmentId, variantId, ownedConfigure)
    return variantId
  }

  function saveDraftIntake(assessmentId: string, draft: AraDraftIntake) {
    commitWorkspace(assessmentId, workspace => ({
      ...workspace,
      draftIntake: cloneDraftIntake(draft)
    }))
  }

  function clearDraftIntake(assessmentId: string) {
    commitWorkspace(assessmentId, workspace => ({
      ...workspace,
      draftIntake: null
    }))
  }

  function stopGeneration(assessmentId: string, variantId: string) {
    const key = controllerKey(assessmentId, variantId)
    generationControllers.current.get(key)?.abort()
    generationControllers.current.delete(key)
    commitWorkspace(assessmentId, workspace => {
      const remaining = workspace.variants.filter(variant => variant.id !== variantId)
      return {
        ...workspace,
        variants: remaining,
        activeVariantId:
          workspace.activeVariantId === variantId
            ? (remaining.at(-1)?.id ?? null)
            : workspace.activeVariantId,
        showConfigure: remaining.length === 0 ? true : workspace.showConfigure
      }
    })
  }

  async function retryGeneration(assessmentId: string, variantId: string): Promise<void> {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const configure = variant?.configure
    if (!configure) return
    await runGeneration(assessmentId, variantId, cloneConfigure(configure))
  }

  async function regenerateVariant(
    assessmentId: string,
    variantId: string,
    configure: AraConfigurePayload
  ): Promise<void> {
    await runGeneration(assessmentId, variantId, cloneConfigure(configure))
  }

  function removeVariant(assessmentId: string, variantId: string) {
    const key = controllerKey(assessmentId, variantId)
    generationControllers.current.get(key)?.abort()
    generationControllers.current.delete(key)
    editControllers.current.get(key)?.abort()
    editControllers.current.delete(key)
    editBuffers.current.delete(key)
    removalUndoStacks.current.delete(key)
    commitWorkspace(assessmentId, workspace => {
      const remaining = workspace.variants.filter(variant => variant.id !== variantId)
      return {
        ...workspace,
        variants: remaining,
        activeVariantId:
          workspace.activeVariantId === variantId
            ? (remaining.at(-1)?.id ?? null)
            : workspace.activeVariantId,
        showConfigure: remaining.length === 0 ? true : workspace.showConfigure
      }
    })
  }

  function clearWorkspace(assessmentId: string) {
    for (const [key, controller] of generationControllers.current) {
      if (key.startsWith(`${assessmentId}::`)) {
        controller.abort()
        generationControllers.current.delete(key)
      }
    }
    for (const [key, controller] of editControllers.current) {
      if (key.startsWith(`${assessmentId}::`)) {
        controller.abort()
        editControllers.current.delete(key)
      }
    }
    for (const key of editBuffers.current.keys()) {
      if (key.startsWith(`${assessmentId}::`)) editBuffers.current.delete(key)
    }
    for (const key of removalUndoStacks.current.keys()) {
      if (key.startsWith(`${assessmentId}::`)) removalUndoStacks.current.delete(key)
    }
    commitWorkspace(assessmentId, () => emptyWorkspace(assessmentId))
    void dbRef.current.deleteWorkspace(assessmentId).catch(() => undefined)
  }

  function setActiveVariant(assessmentId: string, variantId: string | null) {
    commitWorkspace(assessmentId, workspace => ({
      ...workspace,
      activeVariantId: variantId,
      showConfigure: false
    }))
  }

  function openConfigure(assessmentId: string) {
    // A pre-hydration commit would create a fresh in-memory workspace and block
    // the persisted one from being restored (hydration skips existing records),
    // so defer the mode change until the workspace is loaded. Hydrated
    // workspaces commit synchronously.
    if (readyAssessmentsRef.current[assessmentId]) {
      commitWorkspace(assessmentId, workspace => ({
        ...workspace,
        showConfigure: true
      }))
      return
    }
    void hydrateWorkspace(assessmentId).then(() => {
      commitWorkspace(assessmentId, workspace => ({
        ...workspace,
        showConfigure: true
      }))
    })
  }

  function closeConfigure(assessmentId: string) {
    commitWorkspace(assessmentId, workspace => ({
      ...workspace,
      showConfigure: workspace.variants.length === 0
    }))
  }

  function updateEdit(
    assessmentId: string,
    variantId: string,
    update: (edit: AraVariantEditState) => AraVariantEditState,
    options?: { persist?: boolean }
  ) {
    updateVariant(
      assessmentId,
      variantId,
      variant => ({
        ...variant,
        edit: variant.edit ? update(variant.edit) : null
      }),
      options
    )
  }

  function mirrorVariantEditRef(
    assessmentId: string,
    variantId: string,
    sections: Record<string, string>,
    structuredAra: Record<string, unknown>,
    lastRenderedHtml: string
  ) {
    const workspace = workspacesRef.current[assessmentId]
    if (!workspace) return
    workspacesRef.current = {
      ...workspacesRef.current,
      [assessmentId]: mapVariant(workspace, variantId, variant =>
        variant.edit
          ? { ...variant, edit: { ...variant.edit, sections, structuredAra, lastRenderedHtml } }
          : variant
      )
    }
  }

  async function sendEditMessage(
    assessmentId: string,
    variantId: string,
    content: string,
    attachments?: AraAttachment[],
    choices?: { resolvedTarget?: ChatClarifyOption }
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || edit.streaming) return

    const userMessage: ChatUserMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content
    }
    const assistantMessage: ChatAssistantMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      response: '',
      streaming: true,
      error: null,
      proposals: [],
      targetedProposals: [],
      spanProposals: [],
      chartProposals: [],
      removeProposals: [],
      clarify: null
    }
    const previousMessages = edit.messages

    updateEdit(assessmentId, variantId, current => ({
      ...current,
      messages: [...current.messages, userMessage, assistantMessage],
      streaming: true
    }))

    const key = controllerKey(assessmentId, variantId)
    const controller = new AbortController()
    editControllers.current.set(key, controller)
    const outbound = [...previousMessages, userMessage]
    const conversationalMessages: AraEditChatMessage[] = outbound.map((message, index) => {
      const entry: AraEditChatMessage = {
        role: message.role,
        content:
          message.role === 'user'
            ? (message as ChatUserMessage).content
            : (message as ChatAssistantMessage).response
      }
      if (index === outbound.length - 1 && attachments && attachments.length > 0) {
        entry.attachments = attachments
      }
      return entry
    })
    const resolvedTarget = choices?.resolvedTarget
    const target: AraEditTarget | undefined = resolvedTarget
      ? {
          key: '',
          selector: '',
          sectionKey: resolvedTarget.section_key,
          id: resolvedTarget.id,
          type: resolvedTarget.type,
          outer_html: '',
          resolved_id: resolvedTarget.id
        }
      : (edit.selectedElementTarget ?? undefined)
    const scopedSections = target?.sectionKey
      ? { [target.sectionKey]: edit.sections[target.sectionKey] ?? '' }
      : edit.sections
    const request: AraEditChatRequest = {
      assessment_id: assessmentId,
      messages: conversationalMessages,
      sections: scopedSections,
      structured_ara: edit.structuredAra,
      // The sector this report was BUILT with, when an SE pinned one. Was hardcoded null,
      // so the chat could not tell "already corrected to Pharmaceutical" from "never set"
      // and would happily propose a rebuild into the sector it is already using.
      sector: variant?.configure?.sector ?? null,
      ...(target ? { target } : {}),
      language: variant?.configure?.language ?? 'en-US',
      currency: variant?.configure?.currency ?? 'USD'
    }
    let rawResponse = ''

    try {
      for await (const frame of streamAraEdit(request, controller.signal)) {
        if (controller.signal.aborted) break
        if (frame.type === 'text') {
          rawResponse += frame.content
          const response = conversationalAraResponse(rawResponse)
          updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
            ...message,
            response
          }))
        } else if (frame.type === 'edit') {
          const sectionProposal: ChatSectionProposal = { kind: 'section', frame, status: 'idle' }
          updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
            ...message,
            proposals: [...message.proposals, sectionProposal]
          }))
        } else if (frame.type === 'target_edit') {
          const scopedTarget =
            findVariant(workspacesRef.current, assessmentId, variantId)?.edit
              ?.selectedElementTarget ?? target
          const frameTarget = frame.target
            ? { ...frame.target, resolved_id: frame.target.resolved_id ?? frame.target.id }
            : null
          const effectiveTarget = frameTarget ?? scopedTarget
          if (effectiveTarget) {
            const targetedProposal: ChatTargetProposal = {
              kind: 'target',
              targetKey: frame.target_key,
              html: frame.html,
              target: effectiveTarget,
              status: 'idle'
            }
            updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
              ...message,
              targetedProposals: [...message.targetedProposals, targetedProposal]
            }))
          }
        } else if (frame.type === 'span_edit') {
          const scopedTarget =
            findVariant(workspacesRef.current, assessmentId, variantId)?.edit
              ?.selectedElementTarget ?? target
          if (scopedTarget) {
            const spanProposal: ChatSpanProposal = {
              kind: 'span',
              targetKey: frame.target_key,
              text: frame.text,
              target: scopedTarget,
              status: 'idle'
            }
            updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
              ...message,
              spanProposals: [...message.spanProposals, spanProposal]
            }))
          }
        } else if (frame.type === 'chart_edit') {
          const scopedTarget =
            findVariant(workspacesRef.current, assessmentId, variantId)?.edit
              ?.selectedElementTarget ?? target
          const chartProposal = collectChartProposal(frame, scopedTarget)
          if (chartProposal) {
            updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
              ...message,
              chartProposals: [...message.chartProposals, chartProposal]
            }))
          }
        } else if (frame.type === 'sector_options') {
          // A choice, not an action. Nothing rebuilds until the SE picks one; the proposal
          // rides on the assistant message so it renders inline with the explanation.
          updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
            ...message,
            sectorOptions: frame.sectors
          }))
        } else if (frame.type === 'scope_redirect') {
          // A pointer, not an edit. Scope is a generation input owned by the Scope
          // panel; the backend guarantees no edit frame accompanies this one, so the
          // message renders the explanation plus a button that opens the panel.
          updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
            ...message,
            scopeRedirect: true
          }))
        } else if (frame.type === 'regenerate') {
          // An offer, never an action. The model decides on its own whether to commit to a
          // cohort, and committing throws away a finished report plus every unsaved edit and
          // spends ~3 minutes rebuilding — too much to spend on an inference. It rides on the
          // assistant message as a pending card; only `confirmSectorRebuild` starts the run.
          pendSectorRebuild(assessmentId, variantId, assistantMessage.id, frame.sector)
        } else if (frame.type === 'remove') {
          const removeProposal: ChatRemoveProposal = { kind: 'remove', frame, status: 'idle' }
          updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
            ...message,
            removeProposals: [...(message.removeProposals ?? []), removeProposal]
          }))
        } else if (frame.type === 'clarify') {
          updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
            ...message,
            clarify: {
              request: frame.request,
              options: frame.options.map(option => ({ ...option })),
              answeredId: null
            }
          }))
        } else if (frame.type === 'error') {
          updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
            ...message,
            streaming: false,
            error: frame.detail
          }))
          updateEditStreaming(assessmentId, variantId, false)
          notify(assessmentId, 'error', 'background.editFailed')
          return
        } else if (frame.type === 'done') break
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        const detail = error instanceof Error ? error.message : String(error)
        updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
          ...message,
          streaming: false,
          error: detail
        }))
        updateEditStreaming(assessmentId, variantId, false)
        notify(assessmentId, 'error', 'background.editFailed')
        return
      }
    } finally {
      if (editControllers.current.get(key) === controller) {
        editControllers.current.delete(key)
      }
    }

    updateAssistant(assessmentId, variantId, assistantMessage.id, message => ({
      ...message,
      streaming: false
    }))
    updateEditStreaming(assessmentId, variantId, false)
    if (controller.signal.aborted) return
    notify(assessmentId, 'success', 'background.editComplete')
  }

  async function resolveClarifyOption(
    assessmentId: string,
    variantId: string,
    messageId: string,
    option: ChatClarifyOption
  ): Promise<void> {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const clarify = findAssistantMessage(variant, messageId)?.clarify
    if (!clarify || clarify.answeredId) return

    updateAssistant(assessmentId, variantId, messageId, message =>
      message.clarify && !message.clarify.answeredId
        ? { ...message, clarify: { ...message.clarify, answeredId: option.id } }
        : message
    )
    await sendEditMessage(assessmentId, variantId, clarify.request, undefined, {
      resolvedTarget: option
    })
  }

  function updateAssistant(
    assessmentId: string,
    variantId: string,
    messageId: string,
    update: (message: ChatAssistantMessage) => ChatAssistantMessage
  ) {
    updateEdit(assessmentId, variantId, edit => ({
      ...edit,
      messages: edit.messages.map(message =>
        message.id === messageId && message.role === 'assistant'
          ? update(message as ChatAssistantMessage)
          : message
      )
    }))
  }

  function updateEditStreaming(assessmentId: string, variantId: string, streaming: boolean) {
    updateEdit(assessmentId, variantId, edit => ({ ...edit, streaming }))
  }

  function stopEditStreaming(assessmentId: string, variantId: string) {
    const key = controllerKey(assessmentId, variantId)
    editControllers.current.get(key)?.abort()
    editControllers.current.delete(key)
    updateEdit(assessmentId, variantId, edit => ({
      ...edit,
      streaming: false,
      messages: edit.messages.flatMap(message => {
        if (message.role !== 'assistant' || !message.streaming) return [message]
        if (!hasAssistantMessageContent(message)) return []
        return [{ ...message, streaming: false }]
      })
    }))
  }

  async function renderProposalPreview(
    assessmentId: string,
    variantId: string,
    draft: AraDraftPayload,
    edit: AraVariantEditState,
    action: ProposalAction,
    status: ChatProposalStatus
  ): Promise<void> {
    const nextEdit = setProposalActionStatus(edit, action, status)
    const key = controllerKey(assessmentId, variantId)
    editControllers.current.get(key)?.abort()
    editControllers.current.delete(key)

    // Commit a rejection before the async render: a rejection fired right after
    // this one aborts this render, and the status must not depend on it resolving.
    if (status === 'rejected') {
      updateEdit(assessmentId, variantId, currentEdit => {
        const committed = setProposalActionStatus(currentEdit, action, status)
        return pendingPreviewActions(committed).length === 0
          ? { ...committed, previewHtml: null }
          : committed
      })
    }

    if (pendingPreviewActions(nextEdit).length === 0) {
      updateEdit(assessmentId, variantId, currentEdit => ({
        ...setProposalActionStatus(currentEdit, action, status),
        previewHtml: null
      }))
      return
    }

    const preview = applyActivePreviewActions(nextEdit, nextEdit.sectionOrder ?? draft.sectionOrder)
    const controller = new AbortController()
    editControllers.current.set(key, controller)
    const bodyHtml = assembleBodyHtml(preview.sections, preview.sectionOrder ?? draft.sectionOrder)

    try {
      const renderedHtml = await renderAra(
        { structured_ara: preview.structuredAra, body_html: bodyHtml },
        controller.signal
      )
      if (controller.signal.aborted) return
      updateEdit(assessmentId, variantId, currentEdit => {
        const committed = setProposalActionStatus(currentEdit, action, status)
        return {
          ...committed,
          previewHtml: pendingPreviewActions(committed).length === 0 ? null : renderedHtml
        }
      })
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') return
      if (action.kind === 'chart') {
        toast.error(t('chat.proposalRenderFailed'))
        return
      }
      throw error
    } finally {
      if (editControllers.current.get(key) === controller) {
        editControllers.current.delete(key)
      }
    }
  }

  async function acceptProposalAction(
    assessmentId: string,
    variantId: string,
    draft: AraDraftPayload,
    edit: AraVariantEditState,
    action: ProposalAction
  ): Promise<void> {
    const status = proposalActionStatus(edit, action)
    if (status === 'accepted' || status === 'rejected') return

    const acceptedEdit = applyProposalAction(edit, draft.sectionOrder, action)
    const acceptedWithStatus = setProposalActionStatus(acceptedEdit, action, 'accepted')
    // A custom-section accept carries a NEW order (the key inserted after its
    // anchor). It must land in draftPayload — assemble, publish, and the
    // persisted server source all read the draft order — before anything
    // renders from it.
    const effectiveOrder = acceptedEdit.sectionOrder ?? draft.sectionOrder
    if (effectiveOrder !== draft.sectionOrder) {
      updateVariant(assessmentId, variantId, variant =>
        variant.draftPayload
          ? {
              ...variant,
              draftPayload: { ...variant.draftPayload, sectionOrder: [...effectiveOrder] }
            }
          : variant
      )
    }
    const acceptedBody = assembleBodyHtml(acceptedEdit.sections, effectiveOrder)
    // What this accept actually changed, captured before it lands. Scoped to the
    // touched sections rather than the whole body: the server stores a diff, so
    // shipping the untouched sections on both sides is pure waste.
    const changedSections = Object.keys({ ...edit.sections, ...acceptedEdit.sections }).filter(
      sectionKey => edit.sections[sectionKey] !== acceptedEdit.sections[sectionKey]
    )
    const beforeHtml = changedSections.map(k => edit.sections[k] ?? '').join('\n')
    const afterHtml = changedSections.map(k => acceptedEdit.sections[k] ?? '').join('\n')
    const key = controllerKey(assessmentId, variantId)
    editControllers.current.get(key)?.abort()
    const controller = new AbortController()
    editControllers.current.set(key, controller)

    try {
      const acceptedHtml = await renderAra(
        { structured_ara: acceptedEdit.structuredAra, body_html: acceptedBody },
        controller.signal
      )
      if (controller.signal.aborted) return

      const remainingPreviews = pendingPreviewActions(acceptedWithStatus).length > 0
      const preview = remainingPreviews
        ? applyActivePreviewActions(acceptedWithStatus, effectiveOrder)
        : null

      markServerSyncDirty(assessmentId, variantId)
      updateEdit(assessmentId, variantId, currentEdit => ({
        ...setProposalActionStatus(currentEdit, action, 'accepted'),
        sections: acceptedEdit.sections,
        structuredAra: acceptedEdit.structuredAra,
        sectionOrder: acceptedEdit.sectionOrder ?? currentEdit.sectionOrder,
        lastRenderedHtml: acceptedHtml,
        previewHtml: null
      }))

      // Record the accepted edit. AI edits are logged exactly once, here — the
      // service deliberately logs nothing at stream time so rejected proposals
      // and pure Q&A never count as edits. Not awaited: it is fire-and-forget
      // and must not delay the applied edit the user is already looking at.
      void araEditAccept({
        assessment_id: assessmentId,
        kind: action.kind,
        key: proposalSectionKey(action),
        before: beforeHtml,
        after: afterHtml,
        patch: action.kind === 'chart' ? action.proposal.patch : null,
        conversation: conversationForLog(acceptedEdit),
        sector: findVariant(workspacesRef.current, assessmentId, variantId)?.configure?.sector ?? null,
        plant_name: araPlantName(acceptedEdit.structuredAra) || null
      })

      if (!preview) return

      try {
        const previewHtml = await renderAra(
          {
            structured_ara: preview.structuredAra,
            body_html: assembleBodyHtml(preview.sections, preview.sectionOrder ?? effectiveOrder)
          },
          controller.signal
        )
        if (controller.signal.aborted) return
        updateEdit(assessmentId, variantId, currentEdit => ({ ...currentEdit, previewHtml }), {
          persist: false
        })
      } catch (error) {
        if ((error as { name?: string }).name !== 'AbortError') {
          toast.error(t('chat.proposalRenderFailed'))
        }
      }
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') return
      if (action.kind === 'chart') {
        toast.error(t('chat.proposalRenderFailed'))
        return
      }
      throw error
    } finally {
      if (editControllers.current.get(key) === controller) {
        editControllers.current.delete(key)
      }
    }
  }

  async function previewProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: AraEditFrame
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return

    const current = currentSectionProposal(variant, messageId, index)
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'section', messageId, index, proposal },
      current?.status === 'previewing' ? 'idle' : 'previewing'
    )
  }

  async function applyProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: AraEditFrame
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return
    await acceptProposalAction(assessmentId, variantId, draft, edit, {
      kind: 'section',
      messageId,
      index,
      proposal
    })
  }

  async function rejectProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ): Promise<void> {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const current = currentSectionProposal(variant, messageId, index)
    if (current && (current.status === 'accepted' || current.status === 'rejected')) return
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || !current) return
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'section', messageId, index, proposal: current.frame },
      'rejected'
    )
  }

  async function previewTargetProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatTargetProposal
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return

    const current = currentTargetProposal(variant, messageId, index)
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'target', messageId, index, proposal },
      current?.status === 'previewing' ? 'idle' : 'previewing'
    )
  }

  async function applyTargetProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatTargetProposal
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return
    await acceptProposalAction(assessmentId, variantId, draft, edit, {
      kind: 'target',
      messageId,
      index,
      proposal
    })
  }

  async function rejectTargetProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ): Promise<void> {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const current = currentTargetProposal(variant, messageId, index)
    if (current && (current.status === 'accepted' || current.status === 'rejected')) return
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || !current) return
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'target', messageId, index, proposal: current },
      'rejected'
    )
  }

  async function previewSpanProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatSpanProposal
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return

    const current = currentSpanProposal(variant, messageId, index)
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'span', messageId, index, proposal },
      current?.status === 'previewing' ? 'idle' : 'previewing'
    )
  }

  async function applySpanProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatSpanProposal
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return
    await acceptProposalAction(assessmentId, variantId, draft, edit, {
      kind: 'span',
      messageId,
      index,
      proposal
    })
  }

  async function rejectSpanProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ): Promise<void> {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const current = currentSpanProposal(variant, messageId, index)
    if (current && (current.status === 'accepted' || current.status === 'rejected')) return
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || !current) return
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'span', messageId, index, proposal: current },
      'rejected'
    )
  }

  async function previewChartProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatChartProposal
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return

    const current = currentChartProposal(variant, messageId, index)
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'chart', messageId, index, proposal },
      current?.status === 'previewing' ? 'idle' : 'previewing'
    )
  }

  async function applyChartProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number,
    proposal: ChatChartProposal
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return
    await acceptProposalAction(assessmentId, variantId, draft, edit, {
      kind: 'chart',
      messageId,
      index,
      proposal
    })
  }

  /**
   * Rebuild against `sector`. The sector picks the peer cohort behind every failure
   * probability and benchmark, and no edit can recompute those — so this is a fresh run,
   * not a patch. Pinned onto `configure` so a later retry or reload keeps the correction
   * instead of silently reverting to the CRM value that was wrong.
   */
  function startSectorRebuild(assessmentId: string, variantId: string, sector: string): void {
    const current = findVariant(workspacesRef.current, assessmentId, variantId)?.configure
    if (!current) return
    notify(assessmentId, 'success', 'background.regeneratingSector')
    void runGeneration(assessmentId, variantId, { ...cloneConfigure(current), sector })
  }

  function setSectorRebuildStatus(
    assessmentId: string,
    variantId: string,
    messageId: string,
    status: ChatSectorRebuildStatus
  ): void {
    updateAssistant(assessmentId, variantId, messageId, message =>
      message.sectorRebuild
        ? { ...message, sectorRebuild: { ...message.sectorRebuild, status } }
        : message
    )
  }

  function chooseSector(
    assessmentId: string,
    variantId: string,
    messageId: string,
    sector: string,
    dirty: boolean
  ): void {
    // The click IS the choice, so a clean draft goes straight to the rebuild. With unsaved
    // work in the draft the same click would destroy it, which is more than the SE agreed to
    // by picking a cohort — route it through the confirm card instead.
    if (dirty) {
      pendSectorRebuild(assessmentId, variantId, messageId, sector)
      return
    }
    startSectorRebuild(assessmentId, variantId, sector)
  }

  /**
   * Claim an offer for exactly one decision. Returns the pending offer the first time and
   * null on every later call, including a second click in the same React batch.
   */
  function sectorRebuildKey(assessmentId: string, variantId: string, messageId: string): string {
    return `${controllerKey(assessmentId, variantId)}::${messageId}`
  }

  /**
   * Put a fresh offer on a message, releasing any claim a previous offer on that message
   * left behind.
   *
   * The release is what makes "cancel, then pick a different cohort" work. The options
   * buttons stay live after a cancel, so re-picking is an ordinary path — and without this
   * the new offer would render with Rebuild/Cancel buttons that silently do nothing,
   * because the claim is keyed by message and the cancelled offer already took it.
   * Keying by sector instead would not help: cancelling Pharmaceutical and then re-picking
   * Pharmaceutical is the same key again.
   */
  function pendSectorRebuild(
    assessmentId: string,
    variantId: string,
    messageId: string,
    sector: string
  ): void {
    decidedSectorRebuilds.current.delete(sectorRebuildKey(assessmentId, variantId, messageId))
    updateAssistant(assessmentId, variantId, messageId, message => ({
      ...message,
      sectorRebuild: { sector, status: 'pending' }
    }))
  }

  function claimSectorRebuild(
    assessmentId: string,
    variantId: string,
    messageId: string
  ): ChatSectorRebuild | null {
    const key = sectorRebuildKey(assessmentId, variantId, messageId)
    if (decidedSectorRebuilds.current.has(key)) return null
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const pending = findAssistantMessage(variant, messageId)?.sectorRebuild
    // The status check catches a card restored from a persisted workspace, where the
    // in-memory set is empty but the decision was already made in an earlier session.
    if (pending?.status !== 'pending') return null
    decidedSectorRebuilds.current.add(key)
    return pending
  }

  function confirmSectorRebuild(assessmentId: string, variantId: string, messageId: string): void {
    const claimed = claimSectorRebuild(assessmentId, variantId, messageId)
    if (!claimed) return
    setSectorRebuildStatus(assessmentId, variantId, messageId, 'confirmed')
    startSectorRebuild(assessmentId, variantId, claimed.sector)
  }

  function cancelSectorRebuild(assessmentId: string, variantId: string, messageId: string): void {
    if (!claimSectorRebuild(assessmentId, variantId, messageId)) return
    setSectorRebuildStatus(assessmentId, variantId, messageId, 'cancelled')
  }

  async function rejectChartProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ): Promise<void> {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const current = currentChartProposal(variant, messageId, index)
    if (current && (current.status === 'accepted' || current.status === 'rejected')) return
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || !current) return
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'chart', messageId, index, proposal: current },
      'rejected'
    )
  }

  function syncSectionOrder(assessmentId: string, variantId: string, order: string[]) {
    updateVariant(assessmentId, variantId, variant =>
      variant.draftPayload
        ? { ...variant, draftPayload: { ...variant.draftPayload, sectionOrder: [...order] } }
        : variant
    )
  }

  async function applyRemoveIntent(
    assessmentId: string,
    variantId: string,
    intent: AraRemoveIntent,
    options: { fromProposal?: boolean } = {}
  ): Promise<boolean> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || variant?.readOnly || edit.streaming) return false
    if (!options.fromProposal && hasPendingPreview(edit)) {
      toast.error(t('chat.removeBlockedByPreview'))
      return false
    }

    const currentOrder = edit.sectionOrder ?? draft.sectionOrder
    const outcome = applyRemoveIntentToState(edit, intent, currentOrder)
    if (!outcome.ok) return false

    const nextOrder = outcome.sectionOrder ?? currentOrder
    const bodyHtml = assembleBodyHtml(outcome.sections, nextOrder)
    const key = controllerKey(assessmentId, variantId)
    editControllers.current.get(key)?.abort()
    const controller = new AbortController()
    editControllers.current.set(key, controller)
    try {
      const renderedHtml = await renderAra(
        { structured_ara: outcome.structuredAra, body_html: bodyHtml },
        controller.signal
      )
      if (controller.signal.aborted) return false

      const stackKey = controllerKey(assessmentId, variantId)
      const stack = removalUndoStacks.current.get(stackKey) ?? []
      stack.push([outcome.undo])
      removalUndoStacks.current.set(stackKey, stack)

      if (outcome.sectionOrder) syncSectionOrder(assessmentId, variantId, outcome.sectionOrder)
      markServerSyncDirty(assessmentId, variantId)
      updateEdit(assessmentId, variantId, currentEdit => ({
        ...currentEdit,
        sections: outcome.sections,
        structuredAra: outcome.structuredAra,
        sectionOrder: outcome.sectionOrder ?? currentEdit.sectionOrder,
        lastRenderedHtml: renderedHtml,
        previewHtml: null,
        selectedElementTarget: dropOrphanedScope(currentEdit.selectedElementTarget, outcome.sections)
      }))
      mirrorVariantEditRef(
        assessmentId,
        variantId,
        outcome.sections,
        outcome.structuredAra,
        renderedHtml
      )
      return true
    } catch {
      return false
    } finally {
      if (editControllers.current.get(key) === controller) {
        editControllers.current.delete(key)
      }
    }
  }

  /**
   * Batch removal (FE-6248): runs the single-removal reducer over every
   * target in one pass, re-renders once and pushes ONE undo entry so the
   * whole batch rewinds as a single action. Targets the reducer refuses
   * (guarded section roots, unresolvable selectors) are skipped and counted
   * so the caller can warn the user; a batch where nothing applied changes
   * nothing and returns null.
   */
  async function applyBatchRemoveIntent(
    assessmentId: string,
    variantId: string,
    targets: AraRemoveIntent[]
  ): Promise<{ removed: number; skipped: number } | null> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || variant?.readOnly || edit.streaming) return null
    if (targets.length === 0) return null
    if (hasPendingPreview(edit)) {
      toast.error(t('chat.removeBlockedByPreview'))
      return null
    }

    const outcome = applyBatchRemoveToState(edit, targets, edit.sectionOrder ?? draft.sectionOrder)
    if (!outcome.ok) return null

    const bodyHtml = assembleBodyHtml(outcome.sections, edit.sectionOrder ?? draft.sectionOrder)
    const key = controllerKey(assessmentId, variantId)
    editControllers.current.get(key)?.abort()
    const controller = new AbortController()
    editControllers.current.set(key, controller)
    try {
      const renderedHtml = await renderAra(
        { structured_ara: outcome.structuredAra, body_html: bodyHtml },
        controller.signal
      )
      if (controller.signal.aborted) return null

      const stackKey = controllerKey(assessmentId, variantId)
      const stack = removalUndoStacks.current.get(stackKey) ?? []
      stack.push(dedupeUndoSnapshots(outcome.undo))
      removalUndoStacks.current.set(stackKey, stack)

      markServerSyncDirty(assessmentId, variantId)
      updateEdit(assessmentId, variantId, currentEdit => ({
        ...currentEdit,
        sections: outcome.sections,
        structuredAra: outcome.structuredAra,
        lastRenderedHtml: renderedHtml,
        previewHtml: null,
        selectedElementTarget: dropOrphanedScope(currentEdit.selectedElementTarget, outcome.sections)
      }))
      mirrorVariantEditRef(
        assessmentId,
        variantId,
        outcome.sections,
        outcome.structuredAra,
        renderedHtml
      )
      return { removed: outcome.removed, skipped: outcome.skipped }
    } catch {
      return null
    } finally {
      if (editControllers.current.get(key) === controller) {
        editControllers.current.delete(key)
      }
    }
  }

  async function previewRemoveProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ): Promise<void> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit) return
    const proposal = findAssistantMessage(variant, messageId)?.removeProposals?.[index]
    if (!proposal || proposal.status === 'accepted' || proposal.status === 'rejected') return
    await renderProposalPreview(
      assessmentId,
      variantId,
      draft,
      edit,
      { kind: 'remove', messageId, index, proposal },
      proposal.status === 'previewing' ? 'idle' : 'previewing'
    )
  }

  async function acceptRemoveProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ): Promise<boolean> {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const proposal = findAssistantMessage(variant, messageId)?.removeProposals?.[index]
    if (!proposal || proposal.status === 'accepted' || proposal.status === 'rejected') return false
    const applied = await applyRemoveIntent(
      assessmentId,
      variantId,
      removeIntentFromFrame(proposal.frame),
      { fromProposal: true }
    )
    if (!applied) {
      toast.error(t('chat.removeFailed'))
      return false
    }
    updateEdit(assessmentId, variantId, edit =>
      setRemoveProposalStatus(edit, messageId, index, 'accepted')
    )
    return true
  }

  function rejectRemoveProposal(
    assessmentId: string,
    variantId: string,
    messageId: string,
    index: number
  ): void {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const proposal = findAssistantMessage(variant, messageId)?.removeProposals?.[index]
    if (!proposal || proposal.status === 'accepted' || proposal.status === 'rejected') return
    updateEdit(assessmentId, variantId, edit => {
      const next = setRemoveProposalStatus(edit, messageId, index, 'rejected')
      return pendingPreviewActions(next).length === 0 ? { ...next, previewHtml: null } : next
    })
  }

  async function undoRemoveIntent(assessmentId: string, variantId: string): Promise<boolean> {
    flushEdits(assessmentId)
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    const draft = variant?.draftPayload
    const edit = variant?.edit
    if (!draft || !edit || variant?.readOnly || edit.streaming) return false

    if (hasPendingPreview(edit)) {
      toast.error(t('chat.removeBlockedByPreview'))
      return false
    }

    const stackKey = controllerKey(assessmentId, variantId)
    const stack = removalUndoStacks.current.get(stackKey)
    const entry = stack?.at(-1)
    const restored = entry
      ? restoreBatchSnapshots(
          entry,
          edit.sections,
          (edit.structuredAra.text_overrides as Record<string, string> | undefined) ?? {}
        )
      : null
    if (!restored) return false

    const restoredSections = restored.sections
    const restoredStructuredAra = {
      ...edit.structuredAra,
      text_overrides: restored.overrides
    }
    const restoredOrder = restored.order ?? edit.sectionOrder ?? draft.sectionOrder
    const bodyHtml = assembleBodyHtml(restoredSections, restoredOrder)
    const key = controllerKey(assessmentId, variantId)
    editControllers.current.get(key)?.abort()
    const controller = new AbortController()
    editControllers.current.set(key, controller)
    try {
      const renderedHtml = await renderAra(
        { structured_ara: restoredStructuredAra, body_html: bodyHtml },
        controller.signal
      )
      if (controller.signal.aborted) return false

      const pending = removalUndoStacks.current.get(stackKey)
      if (pending) {
        pending.pop()
        if (pending.length === 0) removalUndoStacks.current.delete(stackKey)
      }

      if (restored.order) syncSectionOrder(assessmentId, variantId, restored.order)
      markServerSyncDirty(assessmentId, variantId)
      updateEdit(assessmentId, variantId, currentEdit => ({
        ...currentEdit,
        sections: restoredSections,
        structuredAra: restoredStructuredAra,
        sectionOrder: restored.order ?? currentEdit.sectionOrder,
        lastRenderedHtml: renderedHtml,
        previewHtml: null,
        selectedElementTarget: currentEdit.selectedElementTarget
      }))
      mirrorVariantEditRef(
        assessmentId,
        variantId,
        restoredSections,
        restoredStructuredAra,
        renderedHtml
      )
      return true
    } catch {
      return false
    } finally {
      if (editControllers.current.get(key) === controller) {
        editControllers.current.delete(key)
      }
    }
  }

  function updateInlineSection(assessmentId: string, variantId: string, key: string, html: string) {
    // Buffered, not committed: inline section bodies stay out of React render
    // state until the idle batch applies them (the iframe already shows the
    // edit — the user typed it there). The capture rides the live DOM, so it
    // can carry edit-surface artifacts (picker outline class, contenteditable
    // attrs): strip at intake or they bake into the published snapshot.
    const buffer = ensureEditBuffer(assessmentId, variantId)
    buffer.sectionBodies[key] = stripAraEditArtifacts(html)
    markBufferedServerSyncDirty(assessmentId, variantId)
    schedulePersist(assessmentId)
  }

  // Focus/scope are UI-transient (stripped on persist): persist:false keeps
  // the save indicator on the content-change contract.
  function updateFocusedSection(assessmentId: string, variantId: string, key: string | null) {
    const current = findVariant(workspacesRef.current, assessmentId, variantId)?.edit
    if (current && current.focusedSectionKey === key) return
    updateEdit(assessmentId, variantId, edit => ({ ...edit, focusedSectionKey: key }), {
      persist: false
    })
  }

  function setEditScope(assessmentId: string, variantId: string, target: AraEditTarget) {
    updateEdit(assessmentId, variantId, edit => ({ ...edit, selectedElementTarget: target }), {
      persist: false
    })
  }

  function clearEditScope(assessmentId: string, variantId: string) {
    updateEdit(assessmentId, variantId, edit => ({ ...edit, selectedElementTarget: null }), {
      persist: false
    })
  }

  /**
   * Lifecycle surface updates after publish/unpublish calls: the variant's
   * read-only rendering tracks the server document's status (readOnly was
   * seeded at open time; lifecycle actions re-sync it here).
   *
   * persist:false — this is a UI-state mirror, not a content edit. The
   * default persist:true would arm the autosave debounce and flip the save
   * indicator to 'pending' ("unsaved changes"), which wrongly disables the
   * Cancel affordance right after entering edit mode (FE-5502). The durable
   * readOnly truth is rehydrated from the document status on the next open.
   */
  function setVariantReadOnly(assessmentId: string, variantId: string, readOnly: boolean) {
    updateVariant(assessmentId, variantId, variant => ({ ...variant, readOnly }), {
      persist: false
    })
  }

  function onTextEdit(assessmentId: string, variantId: string, key: string, value: string) {
    const variant = findVariant(workspacesRef.current, assessmentId, variantId)
    if (!variant?.draftPayload || !variant.edit) return

    // Buffered, not committed: each keystroke lands in the per-variant buffer
    // (a plain ref write) instead of setState + renderAra. The idle-batch
    // pipeline applies all buffered overrides once, re-renders the preview
    // once, and persists once — typing never re-renders the panel tree.
    const buffer = ensureEditBuffer(assessmentId, variantId)
    buffer.textOverrides[key] = value
    markBufferedServerSyncDirty(assessmentId, variantId)
    schedulePersist(assessmentId)
  }

  function getWorkspace(assessmentId: string): AraWorkspace {
    return workspaces[assessmentId] ?? emptyWorkspace(assessmentId)
  }

  function readVariantEdit(assessmentId: string, variantId: string): AraVariantEditState | null {
    // Same-tick readers must see content committed moments ago via
    // commitWorkspace's ref mirror (flushEdits → applyEditBuffers): the React
    // snapshot in any caller's closure still lags a render behind.
    return findVariant(workspacesRef.current, assessmentId, variantId)?.edit ?? null
  }

  function isWorkspaceReady(assessmentId: string): boolean {
    return Boolean(readyAssessments[assessmentId])
  }

  return (
    <AraSessionsContext.Provider
      value={{
        workspaces,
        hydrated,
        saveStates,
        isWorkspaceReady,
        getWorkspace,
        readVariantEdit,
        startGeneration,
        stopGeneration,
        retryGeneration,
        regenerateVariant,
        removeVariant,
        clearWorkspace,
        setActiveVariant,
        openConfigure,
        closeConfigure,
        saveDraftIntake,
        clearDraftIntake,
        sendEditMessage,
        resolveClarifyOption,
        stopEditStreaming,
        previewProposal,
        applyProposal,
        rejectProposal,
        previewTargetProposal,
        applyTargetProposal,
        rejectTargetProposal,
        previewSpanProposal,
        applySpanProposal,
        rejectSpanProposal,
        previewChartProposal,
        applyChartProposal,
        rejectChartProposal,
        chooseSector,
        confirmSectorRebuild,
        cancelSectorRebuild,
        previewRemoveProposal,
        acceptRemoveProposal,
        rejectRemoveProposal,
        applyRemoveIntent,
        applyBatchRemoveIntent,
        undoRemoveIntent,
        updateInlineSection,
        updateFocusedSection,
        setEditScope,
        clearEditScope,
        onTextEdit,
        flushEdits,
        resetVariantEdit,
        saveNow,
        openServerDocument,
        setVariantReadOnly
      }}
    >
      {children}
    </AraSessionsContext.Provider>
  )
}

export function useAraSessions() {
  const context = useContext(AraSessionsContext)
  if (!context) throw new Error('useAraSessions must be used within AraSessionsProvider')
  return context
}

export function useAraWorkspace(assessmentId: string) {
  const { workspaces, getWorkspace } = useAraSessions()
  void workspaces
  const workspace = getWorkspace(assessmentId)
  return {
    workspace,
    activeVariant: selectActiveVariant(workspace)
  }
}
